import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Save, 
  X, 
  User, 
  Building, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Briefcase,
  Users,
  Paperclip,
  Target,
  Calendar,
  FileText,
  Plus,
  RefreshCw,
  Search,
  Check,
  Sparkles,
  Tag,
  HelpCircle
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import TagService from '../../components/Tags/TagService';
import entityRelationService from '../../services/entityRelationService';
import { api } from '../../utils/api';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { customerEnrish } from './CustomerEnrish';
import { customerLeadScout } from './CustomerLeadScout';
import EntitySelectionModal from '../../components/EntityManagement/EntitySelectionModal';
import { useAuth } from '../../contexts/AuthContext';
import CustomAttributesEditor from '../../components/CustomAttributes/CustomAttributesEditor';
import {
  extractCustomAttributeFields,
  sanitizeAttributePayload
} from '../../components/CustomAttributes/utils';
import { clearConfigCache, loadEntityConfig, getFormViewFields, isFieldVisible } from '../../utils/entityConfig';
import dataGovernanceService from '../../services/dataGovernanceService';
import { AlertTriangle } from 'lucide-react';
import HelpModal from '../../components/Help/HelpModal';
import { customerHelpContent } from '../../components/Help/helpContent/customers';
import DuplicateWarningModal from './DuplicateWarningModal';
import {
  ValidationSummary,
  FieldError,
  getInputClassName,
  getSelectClassName,
  isEmpty,
  isValidEmail,
  normalizeEmailList,
  scrollToFirstError
} from '../../components/Forms/FormValidation';
import AutocompleteTextField from '../../components/Forms/AutocompleteTextField';
import { getSuggestionConfig } from '../../components/Forms/TextFieldWithSuggestions';

// Helper function to convert UTC date to local date string for HTML date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // Create a Date object from the UTC string
    const date = new Date(dateString);
    // Get the local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Return in YYYY-MM-DD format for HTML date input
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('CustomerForm: Failed to format date', error);
    return '';
  }
};

const CustomerForm = ({ customer: propCustomer, mode: propMode, onSubmit, onCancel, hiddenFields: propHiddenFields }) => {
  const { t, i18n, safeT } = useTranslationReady();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};
  const preselectedCompany = locationState.preselectedCompany;
  const preselectedCustomerType = locationState.customerType;
  const prefillData = locationState.prefillData;
  
  const initialPreselectedCompanyRef = useRef(preselectedCompany || null);
  const { user } = useAuth();
  const tenantCode = user?.tenantCode || null;

  useEffect(() => {
    if (preselectedCompany) {
      initialPreselectedCompanyRef.current = preselectedCompany;
    }
  }, [preselectedCompany]);

  const effectivePreselectedCompany = initialPreselectedCompanyRef.current;
  const launchedFromCompanyDetail = !!effectivePreselectedCompany;
  
  // Load entity configuration with proper caching and loading states
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('customer');
  
  // Determine mode and customer data
  const mode = propMode || (id ? 'edit' : 'create');
  const [customer, setCustomer] = useState(propCustomer);
  const [loading, setLoading] = useState(mode === 'edit' && !propCustomer);
  const [error, setError] = useState(null);
  const [hiddenFields, setHiddenFields] = useState(propHiddenFields || []);
  const autoEnrichTriggeredRef = useRef(false);
  const preselectAppliedRef = useRef(false);
  const prefillDataAppliedRef = useRef(false);
  const defaultSalespersonSetRef = useRef(false);
  const [customAttributeFields, setCustomAttributeFields] = useState([]);
  const [attributes, setAttributes] = useState({});
  const [showHelpModal, setShowHelpModal] = useState(false);

  const parseAttributes = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('CustomerForm: Failed to parse attributes JSON', error);
        return {};
      }
    }
    return {};
  };

  // Fetch customer data if in edit mode and no customer prop provided
  useEffect(() => {
    if (mode === 'edit' && !propCustomer && id) {
      fetchCustomerData();
    }
  }, [mode, propCustomer, id]);

  useEffect(() => {
    if (mode === 'edit' && customer) {
      const parsedAttributes = parseAttributes(customer?.Attributes ?? customer?.attributes);
      setAttributes(parsedAttributes);
    } else if (mode === 'create') {
      setAttributes({});
    }
  }, [mode, customer]);

  // Load entity configuration including hidden fields
  useEffect(() => {
    // Check for undefined/null/empty array
    if (entityConfig && (propHiddenFields === undefined || propHiddenFields === null || (Array.isArray(propHiddenFields) && propHiddenFields.length === 0))) {
      // Extract hidden fields from config
      // The entityConfig returned from useEntityConfig hook already has hiddenFields at root level
      const configHiddenFields = entityConfig.hiddenFields || [];
      if (Array.isArray(configHiddenFields)) {
        setHiddenFields(configHiddenFields);
        console.log('✅ CustomerForm: Loaded hidden fields from config:', configHiddenFields);
      } else {
        console.warn('⚠️ CustomerForm: hiddenFields is not an array:', configHiddenFields);
        setHiddenFields([]);
      }
    } else if (propHiddenFields) {
      setHiddenFields(propHiddenFields);
      console.log('✅ CustomerForm: Using prop hiddenFields:', propHiddenFields);
    }
  }, [entityConfig, propHiddenFields]);

  useEffect(() => {
    if (entityConfig) {
      const extractedFields = extractCustomAttributeFields(entityConfig, {
        tenantCode,
        includeGlobal: true
      });
      setCustomAttributeFields(extractedFields);
    }
  }, [entityConfig, tenantCode]);

  useEffect(() => {
    if (customAttributeFields.length === 0) return;

    setAttributes((previous) => {
      const next = { ...previous };
      let changed = false;

      customAttributeFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = '';
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [customAttributeFields]);

  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };

  // Check if field should be visible in form based on entity config
  const isFieldVisibleInForm = (fieldName) => {
    // First check if field is in hiddenFields array
    if (isFieldHidden(fieldName)) {
      return false;
    }
    
    // Then check entity config UI visibility settings
    const config = entityConfig?.config || entityConfig;
    if (config?.fields?.[fieldName]) {
      const fieldConfig = config.fields[fieldName];
      // If showInForm is explicitly false, hide the field
      if (fieldConfig.ui?.showInForm === false) {
        return false;
      }
    }
    
    return true;
  };

  // Get dynamically rendered fields from entity config (fields not already hardcoded)
  const getDynamicFormFields = () => {
    if (!entityConfig) return [];
    
    // The entityConfig returned from useEntityConfig hook is already the config object
    if (!entityConfig?.fields) return [];
    
    // List of fields that are already hardcoded in the form
    const hardcodedFields = new Set([
      'CustomerID', 'CustomerCode', 'CombinationName', 'CustomerType', 'PersonID', 'CompanyID', 'PersonCompanyID',
      'FirstName', 'LastName', 'FirstName_Eng', 'LastName_Eng', 'PersonEmail', 'PersonPhone',
      'PersonTitle', 'Title', 'JobTitle', 'Department', 'NickName', 'CompanyName', 'CompanyName_Eng', 'Industry', 'CompanyAddress',
      'City', 'Country', 'CompanyPhone', 'Website', 'Branch', 'CompanyEmail', 'SalespersonID', 'Status', 'Priority',
      'Notes', 'CustomerSummary', 'CreatedAt', 'UpdatedAt', 'Attributes', 'attributes',
      'Tags', 'tags', 'Tag', 'tag'
    ]);
    
    // Get all form fields from config
    const allFormFields = getFormViewFields(entityConfig);
    
      // Filter out hardcoded fields and custom attributes (handled separately)
    return allFormFields.filter(field => {
      // Skip if already hardcoded
      if (hardcodedFields.has(field.name)) return false;
      
      // Skip custom attributes (handled by CustomAttributesEditor)
      if (field._source === 'tenant_custom' || field._tenantSpecific) return false;
      
      // Skip if hidden
      if (isFieldHidden(field.name)) return false;
      
      // Must explicitly have showInForm === true (not just not false)
      if (field.ui?.showInForm !== true) return false;
      
      // Skip fields from "Personal Information" group - they should be in hardcoded section
      const fieldGroup = field.group || field.ui?.group || '';
      if (fieldGroup === 'Personal Information' && customerType === 'Person') return false;
      
      // Check database table to ensure Person fields only show for Person type
      // and Company fields only show for Company type
      const dbTable = field.database?.table;
      if (dbTable === 'Persons' && customerType !== 'Person') return false;
      if (dbTable === 'Companies' && customerType !== 'Company') return false;
      
      // Check conditional visibility
      const formData = {
        CustomerType: customerType,
        ...personData,
        ...companyData,
        ...customer
      };
      if (!isFieldVisible(field, formData)) return false;
      
      return true;
    });
  };

  // Helper to map entity config field name to personData/companyData key
  const mapFieldNameToDataKey = (fieldName, dbTable) => {
    // Special mappings for known fields
    const fieldMappings = {
      'Persons': {
        'PersonEmail': 'email',
        'PersonPhone': 'phone',
        'FirstName': 'firstName',
        'LastName': 'lastName',
        'FirstName_Eng': 'firstName_eng',
        'LastName_Eng': 'lastName_eng',
        'PersonTitle': 'title',
        'Title': 'title',
        'JobTitle': 'jobTitle',
        'Department': 'department',
        'NickName': 'nickName'
      },
      'Companies': {
        'CompanyName': 'companyName',
        'CompanyName_Eng': 'companyName_eng',
        'CompanyPhone': 'phone',
        'Industry': 'industry',
        'CompanyAddress': 'address',
        'City': 'city',
        'Country': 'country',
        'Website': 'website',
        'Branch': 'branch',
        'CompanyEmail': 'companyEmail'
      }
    };
    
    if (fieldMappings[dbTable] && fieldMappings[dbTable][fieldName]) {
      return fieldMappings[dbTable][fieldName];
    }
    
    // Default: convert PascalCase to camelCase
    // e.g., "JobTitle" -> "jobTitle", "FirstName" -> "firstName"
    return fieldName.charAt(0).toLowerCase() + fieldName.slice(1).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  // Render a dynamic form field
  const renderDynamicFormField = (field) => {
    const fieldName = field.name;
    const fieldConfig = field;
    const dbTable = fieldConfig.database?.table;
    
    // Get value from the appropriate data source based on database table
    let value = '';
    let dataKey = fieldName;
    
    if (dbTable === 'Persons') {
      dataKey = mapFieldNameToDataKey(fieldName, 'Persons');
      value = personData[dataKey] || customer?.[fieldName] || '';
    } else if (dbTable === 'Companies') {
      dataKey = mapFieldNameToDataKey(fieldName, 'Companies');
      value = companyData[dataKey] || customer?.[fieldName] || '';
    } else {
      // Default to customer data (use original field name)
      value = customer?.[fieldName] || '';
    }
    
    const isRequired = fieldConfig.validation?.required;
    const inputType = fieldConfig.ui?.inputType || 'text';
    const placeholder = fieldConfig.ui?.placeholder || '';
    const displayName = fieldConfig.displayName || fieldName;
    const displayNameTH = fieldConfig.displayNameTH || displayName;
    const isThai = i18n?.language?.toLowerCase().startsWith('th');
    const label = isThai && displayNameTH ? displayNameTH : displayName;
    
    const handleChange = (newValue) => {
      // Determine which data object to update based on field's database table
      if (dbTable === 'Persons') {
        handlePersonChange(dataKey, newValue);
      } else if (dbTable === 'Companies') {
        handleCompanyChange(dataKey, newValue);
      } else {
        // Default to customer data
        setCustomer(prev => ({ ...prev, [fieldName]: newValue }));
      }
    };
    
    const commonClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
    
    switch (inputType) {
      case 'textarea':
        return (
          <div key={fieldName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {label} {isRequired && <span className="text-red-600">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={commonClasses}
              rows={fieldConfig.ui?.rows || 3}
              placeholder={placeholder}
              required={isRequired}
            />
          </div>
        );
      
      case 'select':
        return (
          <div key={fieldName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {label} {isRequired && <span className="text-red-600">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={commonClasses}
              required={isRequired}
            >
              <option value="">{placeholder || 'Select...'}</option>
              {fieldConfig.ui?.options?.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || opt.value}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'checkbox':
        return (
          <div key={fieldName} className="mb-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={value === true || value === 'true' || value === 1}
                onChange={(e) => handleChange(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {label} {isRequired && <span className="text-red-600">*</span>}
              </span>
            </label>
          </div>
        );
      
      case 'date':
      case 'datetime':
        return (
          <div key={fieldName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {label} {isRequired && <span className="text-red-600">*</span>}
            </label>
            <input
              type={inputType === 'datetime' ? 'datetime-local' : 'date'}
              value={formatDateForInput(value)}
              onChange={(e) => handleChange(e.target.value)}
              className={commonClasses}
              required={isRequired}
            />
          </div>
        );
      
      case 'number':
        return (
          <div key={fieldName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {label} {isRequired && <span className="text-red-600">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={commonClasses}
              placeholder={placeholder}
              required={isRequired}
              min={fieldConfig.validation?.min}
              max={fieldConfig.validation?.max}
            />
          </div>
        );
      
      default:
        return (
          <div key={fieldName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {label} {isRequired && <span className="text-red-600">*</span>}
            </label>
            <input
              type={inputType}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={commonClasses}
              placeholder={placeholder}
              required={isRequired}
              maxLength={fieldConfig.validation?.maxLength}
            />
          </div>
        );
    }
  };

  // Load existing relations for Customer (edit mode)
  useEffect(() => {
    const loadRelations = async () => {
      if (!(mode === 'edit' && customer && customer.CustomerID)) return;
      try {
        const relations = await entityRelationService.getRelatedEntities('Customer', customer.CustomerID, { direction: 'both', timeoutMs: 12000 });
        const mapped = (relations.relations || []).map(rel => {
          const srcType = rel.SourceEntityType || rel.sourceEntityType;
          const tgtType = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isCustomerSource = (srcType || '').toString().toLowerCase() === 'customer';
          const type = isCustomerSource ? tgtType : srcType;
          const id = isCustomerSource ? tgtId : srcId;
          return { type, id, name: '' };
        });

        // Cache initial keys
        initialRelationKeysRef.current = new Set(mapped.map(e => `${e.type}:${e.id}`));

        // Enrich names for display
        const enriched = await Promise.all(mapped.map(async (e) => {
          try {
            let endpoint = '';
            if (e.type === 'Lead') endpoint = `/leads/${e.id}`;
            else if (e.type === 'Opportunity') endpoint = `/opportunities/${e.id}`;
            else if (e.type === 'Activity') endpoint = `/activities/${e.id}`;
            else if (e.type === 'SaleNote') endpoint = `/sales-notes/${e.id}`;
            if (!endpoint) return { ...e, name: `${e.type} ${e.id}` };
            const data = await api.get(endpoint);
            const entity = data.lead || data.opportunity || data.activity || data.saleNote || data.salesNote || data;
            return { ...e, name: getEntityName(entity, e.type) };
          } catch {
            return { ...e, name: `${e.type} ${e.id}` };
          }
        }));
        setRelatedEntities(enriched);
      } catch (err) {
        console.warn('CustomerForm: Failed to load existing relations', err);
      }
    };
    loadRelations();
  }, [mode, customer]);

  // Helper function to check if any field in a group is visible
  const hasVisibleFieldsInGroup = (fieldsInGroup) => {
    return fieldsInGroup.some(fieldName => !isFieldHidden(fieldName));
  };

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/customers/${id}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      if (!response.ok) throw new Error('Failed to fetch customer');
      const data = await response.json();
      setCustomer(data.customer || data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching customer:', err);
    } finally {
      setLoading(false);
    }
  };
  // Company association for Person type
  const [companiesList, setCompaniesList] = useState([]);
  // Salesperson selection
  const [salespersonsList, setSalespersonsList] = useState([]);
  const [selectedSalespersonId, setSelectedSalespersonId] = useState('');
  const [dataGovernancePolicy, setDataGovernancePolicy] = useState(null);
  const [showSalespersonWarning, setShowSalespersonWarning] = useState(false);
  const [originalSalespersonID, setOriginalSalespersonID] = useState(null);
  // Pre-select existing associated company when editing
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    if (effectivePreselectedCompany?.companyId != null) {
      return String(effectivePreselectedCompany.companyId);
    }
    return '';
  });
  const [showNewCompany, setShowNewCompany] = useState(false);

  const [customerType, setCustomerType] = useState(
    preselectedCustomerType || customer?.CustomerType || 'Person'
  );

  useEffect(() => {
    if (!launchedFromCompanyDetail || preselectAppliedRef.current) return;
    if (mode !== 'create') return;

    preselectAppliedRef.current = true;

    setCustomerType('Person');

    if (effectivePreselectedCompany?.companyId != null) {
      setSelectedCompanyId(String(effectivePreselectedCompany.companyId));
    }

    setShowNewCompany(false);

    if (locationState && (locationState.preselectedCompany || locationState.customerType)) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [launchedFromCompanyDetail, mode, effectivePreselectedCompany, locationState, navigate, location.pathname]);

  // Handle prefillData from email hover popover
  useEffect(() => {
    if (prefillData && mode === 'create' && !prefillDataAppliedRef.current) {
      prefillDataAppliedRef.current = true;
      
      // Set customer type to Person if not already set
      if (!preselectedCustomerType) {
        setCustomerType('Person');
      }
      
      // Apply prefillData to personData - use prefillData values if available
      setPersonData(prev => {
        const updated = {
          ...prev,
          email: prefillData.email || prev.email,
          firstName: prefillData.firstName || prev.firstName,
          lastName: prefillData.lastName || prev.lastName,
          firstName_eng: prefillData.firstName_eng || prev.firstName_eng,
          lastName_eng: prefillData.lastName_eng || prev.lastName_eng,
          phone: prefillData.phone || prev.phone,
          title: prefillData.title || prev.title,
          jobTitle: prefillData.jobTitle || prev.jobTitle,
          department: prefillData.department || prev.department
        };
        console.log('Updated personData:', updated);
        return updated;
      });

      // Apply company data if available
      if (prefillData.companyName) {
        setCompanyData(prev => ({
          ...prev,
          companyName: prefillData.companyName || prev.companyName,
          companyName_eng: prefillData.companyName_eng || prev.companyName_eng,
          industry: prefillData.industry || prev.industry,
          website: prefillData.website || prev.website,
          address: prefillData.address || prev.address,
          city: prefillData.city || prev.city,
          country: prefillData.country || prev.country
        }));
      }
      
      // DON'T clear the navigation state - it causes remount!
      // The prefillDataAppliedRef prevents duplicate applications
    }
  }, [prefillData, mode, preselectedCustomerType, navigate, location.pathname, locationState]);

  const [formLoading, setFormLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [enriching, setEnriching] = useState(false);
  const [customerSummary, setCustomerSummary] = useState(customer?.CustomerSummary || '');
  const [enrichmentReport, setEnrichmentReport] = useState({
    isOpen: false,
    items: [],
    searchTerms: [],
    sources: ''
  });
  
  // Lead Scout state
  const [scouting, setScouting] = useState(false);
  // Related Entities state (Lead, Opportunity, Activity, SaleNote)
  const [relatedEntities, setRelatedEntities] = useState([]); // {type, id, name}[]
  const initialRelationKeysRef = useRef(new Set());
  const [entitySelectionModal, setEntitySelectionModal] = useState({
    isOpen: false,
    entityType: null,
    searchTerm: '',
    results: [],
    loading: false,
    selectedIds: []
  });
  const [searchTimeouts, setSearchTimeouts] = useState({});
  
  // Company salesperson suggestion state
  const [companySalespersonSuggestion, setCompanySalespersonSuggestion] = useState(null);
  
  // Associated persons for company
  const [associatedPersons, setAssociatedPersons] = useState([]);
  const [loadingAssociatedPersons, setLoadingAssociatedPersons] = useState(false);
  
  // Confirmation dialog for saving company as customer
  const [saveCompanyDialog, setSaveCompanyDialog] = useState({
    isOpen: false,
    pendingFormData: null
  });
  
  // Duplicate warning dialog
  const [duplicateWarning, setDuplicateWarning] = useState({
    show: false,
    duplicates: [],
    pendingFormData: null
  });
  
  // Merge with existing customer dialog (for field comparison)
  const [mergeWithExisting, setMergeWithExisting] = useState({
    show: false,
    existingCustomer: null,
    pendingFormData: null,
    fieldSelections: {}
  });
  
  // Dynamic list values
  const [industryOptions, setIndustryOptions] = useState([]);
  const [customerTypeOptions, setCustomerTypeOptions] = useState([
    { value: 'Person', label: 'Individual', icon: 'User' },
    { value: 'Company', label: 'Company', icon: 'Building' }
  ]);

  // Fetch companies for association
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const res = await fetch('/api/customers?type=Company', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (!res.ok) throw new Error('Failed to load companies');
        const data = await res.json();
        let companies = (data.customers || []).map(customer => ({
          CompanyID: customer.CompanyID,
          CompanyName: customer.CompanyName || customer.CompanyName_Eng || 'Unnamed Company',
          Branch: customer.Branch || '',
          Industry: customer.Industry || '',
          Address: customer.CompanyAddress || '',
          City: customer.City || '',
          Country: customer.Country || '',
          Phone: customer.Phone || '',
          Website: customer.Website || ''
        }));

        if (effectivePreselectedCompany && effectivePreselectedCompany.companyId != null) {
          const exists = companies.some(c => String(c.CompanyID) === String(effectivePreselectedCompany.companyId));
          if (!exists) {
            companies = [
              ...companies,
              {
                CompanyID: effectivePreselectedCompany.companyId,
                CompanyName: effectivePreselectedCompany.companyName || 'Unnamed Company',
                CompanyName_Eng: effectivePreselectedCompany.companyName_eng || '',
                Branch: effectivePreselectedCompany.branch || '',
                Industry: '',
                Address: '',
                City: '',
                Country: '',
                Phone: '',
                Website: ''
              }
            ];
          }
        }

        setCompaniesList(companies || []);
      } catch (err) {
        console.error('Fetch companies error:', err);
      }
    };
    fetchCompanies();
  }, [effectivePreselectedCompany]);

  // Reset default salesperson ref when mode changes
  useEffect(() => {
    if (mode === 'create') {
      defaultSalespersonSetRef.current = false;
    }
  }, [mode]);

  // Fetch salespersons and data governance policy
  useEffect(() => {
    const fetchSalespersons = async () => {
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const res = await fetch('/api/users/salespersons', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (!res.ok) throw new Error('Failed to load salespersons');
        const data = await res.json();
        // Filter to only include users with Salesperson role (exclude Admin-only users)
        const salespersonOnly = (data.salespersons || []).filter(user => {
          const roles = user.Roles || '';
          // Include if user has Salesperson role (may also have Admin, but must have Salesperson)
          return roles.includes('Salesperson');
        });
        setSalespersonsList(salespersonOnly);
        
        // Debug: Log available salespersons
        console.log('Available salespersons:', salespersonOnly.map(sp => ({
          UserID: sp.UserID,
          FirstName: sp.FirstName,
          LastName: sp.LastName,
          Roles: sp.Roles
        })));
        
        // Set current user as default salesperson in create mode (if user is a salesperson and no salesperson is selected)
        // CRITICAL: Use tenantUserId (not UserID) to match with salesperson's UserID
        // The backend uses tenantUserId for tenant-specific operations
        if (mode === 'create' && user && !selectedSalespersonId && !defaultSalespersonSetRef.current) {
          const userTenantId = user.tenantUserId || user.tenantUserID || user.UserID || user.id || user.userId;
          console.log('Checking for current user as salesperson:', {
            userTenantId,
            userObject: { tenantUserId: user.tenantUserId, UserID: user.UserID, id: user.id, userId: user.userId },
            salespersonIds: salespersonOnly.map(sp => sp.UserID)
          });
          
          const currentUserAsSalesperson = salespersonOnly.find(sp => 
            String(sp.UserID) === String(userTenantId)
          );
          
          if (currentUserAsSalesperson) {
            console.log('Setting current user as default salesperson:', {
              salespersonUserID: currentUserAsSalesperson.UserID,
              userTenantId,
              salespersonName: `${currentUserAsSalesperson.FirstName} ${currentUserAsSalesperson.LastName}`
            });
            setSelectedSalespersonId(String(currentUserAsSalesperson.UserID));
            defaultSalespersonSetRef.current = true;
          } else {
            console.warn('Current user not found in salespersons list:', {
              userTenantId,
              availableSalespersonIds: salespersonOnly.map(sp => sp.UserID),
              salespersonNames: salespersonOnly.map(sp => `${sp.FirstName} ${sp.LastName}`)
            });
          }
        }
        
        // Note: Validation of selectedSalespersonId is handled separately to avoid interfering with user selection
      } catch (err) {
        console.error('Fetch salespersons error:', err);
      }
    };
    
    const fetchDataGovernancePolicy = async () => {
      try {
        const policy = await dataGovernanceService.getPolicy('Customer');
        setDataGovernancePolicy(policy);
        console.log('🔍 CustomerForm: Data governance policy loaded:', policy);
      } catch (error) {
        console.error('Error fetching data governance policy:', error);
      }
    };
    
    fetchSalespersons();
    fetchDataGovernancePolicy();
  }, [mode, user]);

  // Effect to set salesperson ID after both customer data and salesperson list are loaded
  useEffect(() => {
    if (mode === 'edit' && customer && salespersonsList.length > 0) {
      const salespersonId = customer.SalespersonID || '';
      if (salespersonId) {
        const salespersonExists = salespersonsList.some(sp => String(sp.UserID) === String(salespersonId));
        if (salespersonExists) {
          console.log('Setting valid salesperson ID:', salespersonId);
          setSelectedSalespersonId(salespersonId);
          setOriginalSalespersonID(String(salespersonId));
        } else {
          console.warn('Customer has invalid salesperson ID, not setting:', salespersonId);
          console.warn('Available salesperson IDs:', salespersonsList.map(sp => sp.UserID));
        }
      } else {
        setOriginalSalespersonID(null);
      }
    } else if (mode === 'create') {
      setOriginalSalespersonID(null);
    }
  }, [mode, customer, salespersonsList]);

  // Validate selected salesperson only when salespersonsList changes (not when selectedSalespersonId changes)
  useEffect(() => {
    if (selectedSalespersonId && String(selectedSalespersonId).trim() !== '' && salespersonsList.length > 0) {
      const salespersonExists = salespersonsList.some(sp => String(sp.UserID) === String(selectedSalespersonId));
      if (!salespersonExists) {
        console.warn('Current selected salesperson is no longer available, clearing selection');
        console.warn('Selected salesperson ID:', selectedSalespersonId);
        console.warn('Available salesperson IDs:', salespersonsList.map(sp => sp.UserID));
        setSelectedSalespersonId('');
        setErrors(prev => ({
          ...prev,
          salesperson: 'Previously selected salesperson is no longer available. Please select a new one.'
        }));
      } else if (errors.salesperson) {
        // Clear error if salesperson is now valid
        setErrors(prev => ({
          ...prev,
          salesperson: null
        }));
      }
    }
  }, [salespersonsList]); // Only depend on salespersonsList, not selectedSalespersonId

  // Fetch associated persons when editing a company customer
  useEffect(() => {
    const fetchAssociatedPersons = async () => {
      if (mode !== 'edit' || !customer || customerType !== 'Company' || !customer.CustomerID) {
        setAssociatedPersons([]);
        return;
      }

      try {
        setLoadingAssociatedPersons(true);

        const directResponse = await api.get(`/customers/${customer.CustomerID}/associated-persons`);
        const primary = Array.isArray(directResponse?.associatedPersons)
          ? directResponse.associatedPersons
          : [];

        if (primary.length > 0) {
          setAssociatedPersons(primary);
          return;
        }

        const fallbackParams = new URLSearchParams({
          page: '1',
          limit: '200',
          type: 'Person',
          sortBy: 'CreatedAt',
          sortOrder: 'DESC'
        });

        const selectedRole = localStorage.getItem('selectedRole');
        const fallbackResponse = await fetch(`/api/customers?${fallbackParams.toString()}`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });

        if (!fallbackResponse.ok) {
          setAssociatedPersons([]);
          return;
        }

        const fallbackData = await fallbackResponse.json();
        const persons = (fallbackData.customers || []).filter(p => {
          const personCompanyId = p.PersonCompanyID || p.CompanyID;
          return personCompanyId && Number(personCompanyId) === Number(customer.CustomerID);
        });

        setAssociatedPersons(persons);
      } catch (error) {
        console.error('Error fetching associated persons:', error);
        setAssociatedPersons([]);
      } finally {
        setLoadingAssociatedPersons(false);
      }
    };

    fetchAssociatedPersons();
  }, [mode, customer, customerType, customer?.CustomerID]);

  // Check for salesperson suggestion from company's persons
  useEffect(() => {
    const checkCompanySalesperson = async () => {
      if (mode !== 'edit' || !customer || customerType !== 'Company' || !customer.CustomerID) {
        setCompanySalespersonSuggestion(null);
        return;
      }

      // Use already fetched associated persons if available
      if (associatedPersons.length > 0) {
        // Filter persons with salesperson assigned
        const personsWithSalesperson = associatedPersons.filter(p => p.SalespersonID);
        
        if (personsWithSalesperson.length === 0) {
          setCompanySalespersonSuggestion(null);
          return;
        }
        
        // Check if all persons have the same salesperson
        const firstSalespersonId = personsWithSalesperson[0].SalespersonID;
        const allSameSalesperson = personsWithSalesperson.every(p => 
          String(p.SalespersonID) === String(firstSalespersonId)
        );
        
        if (allSameSalesperson && String(firstSalespersonId) !== String(selectedSalespersonId)) {
          // Find salesperson details
          const salesperson = salespersonsList.find(sp => String(sp.UserID) === String(firstSalespersonId));
          if (salesperson) {
            setCompanySalespersonSuggestion({
              salespersonId: firstSalespersonId,
              salespersonName: `${salesperson.FirstName} ${salesperson.LastName}`,
              personCount: personsWithSalesperson.length
            });
          }
        } else {
          setCompanySalespersonSuggestion(null);
        }
      } else {
        setCompanySalespersonSuggestion(null);
      }
    };

    if (salespersonsList.length > 0 && associatedPersons.length > 0) {
      checkCompanySalesperson();
    }
  }, [mode, customer, customerType, customer?.CustomerID, salespersonsList, selectedSalespersonId, associatedPersons]);

  // Load dynamic list values from entity config
  useEffect(() => {
    if (!entityConfig) return;
    
    try {
      // The entityConfig returned from useEntityConfig hook is already the config object
      console.log('🔍 CustomerForm: Loading list values from entity config:', {
        hasIndustry: !!entityConfig?.fields?.Industry,
        industryOptions: entityConfig?.fields?.Industry?.ui?.options,
        hasCustomerType: !!entityConfig?.fields?.CustomerType,
        customerTypeOptions: entityConfig?.fields?.CustomerType?.ui?.options
      });
      
      // Update Industry options if available in config
      if (entityConfig?.fields?.Industry?.ui?.options) {
        setIndustryOptions(entityConfig.fields.Industry.ui.options);
      }
      
      // Update CustomerType options if available in config
      if (entityConfig?.fields?.CustomerType?.ui?.options) {
        setCustomerTypeOptions(entityConfig.fields.CustomerType.ui.options);
      }
    } catch (error) {
      console.error('❌ CustomerForm: Failed to load list values:', error);
      // Keep using default options if loading fails
    }
  }, [entityConfig]);

    // Update selection when editing existing customer
  useEffect(() => {
    console.log('=== CUSTOMER UPDATE EFFECT TRIGGERED ===');
    console.log('Mode:', mode);
    console.log('Customer:', customer);
    console.log('Customer SalespersonID:', customer?.SalespersonID);
    console.log('Current selectedCompanyId:', selectedCompanyId);
    
    if (mode === 'edit' && customer) {
      // Reset company association when customer changes
      const companyId = customer.CompanyID ? String(customer.CompanyID) : '';
      console.log('Setting selectedCompanyId to:', companyId);
      console.log('Customer CompanyID:', customer.CompanyID);
      console.log('Customer CompanyName:', customer.CompanyName);
      
      setSelectedCompanyId(companyId);
      setCustomerType(customer.CustomerType || 'Person');
      setCustomerSummary(customer.CustomerSummary || '');
      setCustomerCode(customer.CustomerCode || '');
      setCombinationName(customer.CombinationName || '');
      
      // Update person data
      setPersonData({
        title: customer.PersonTitle || '',
        nickName: customer.NickName || '',
        firstName: customer.FirstName || '',
        lastName: customer.LastName || '',
        firstName_eng: customer.FirstName_Eng || '',
        lastName_eng: customer.LastName_Eng || '',
        jobTitle: customer.JobTitle || '',
        department: customer.Department || '',
        email: customer.PersonEmail || '',
        phone: customer.PersonPhone || ''
      });
      
      // Update company data
      setCompanyData({
        companyName: customer.CompanyName || '',
        companyName_eng: customer.CompanyName_Eng || '',
        industry: customer.Industry || '',
        address: customer.CompanyAddress || customer.Address || '',
        city: customer.City || '',
        country: customer.Country || '',
        phone: customer.Phone || '',
        website: customer.Website || '',
        branch: customer.Branch || '',
        companyEmail: customer.CompanyEmail || '',
        taxID: customer.TaxID || ''
      });

      // Debug logging for company association
      if (process.env.NODE_ENV === 'development') {
        console.log('Customer edit mode - Company association:', {
          customerId: customer.CustomerID,
          companyId: customer.CompanyID,
          companyName: customer.CompanyName,
          selectedCompanyId: companyId,
          customerType: customer.CustomerType
        });
      }
    } else if (mode === 'create') {
      // Reset form when creating new customer, UNLESS we have prefillData
      if (prefillData) {
        // Don't reset the form if we have prefillData from email hover
        return;
      }

      if (!launchedFromCompanyDetail) {
      setSelectedCompanyId('');
      setShowNewCompany(false);
      } else if (effectivePreselectedCompany?.companyId != null) {
        setSelectedCompanyId(String(effectivePreselectedCompany.companyId));
        setShowNewCompany(false);
      }

      setSelectedSalespersonId('');
      setCustomerSummary('');
      setCustomerCode('');
      setCombinationName('');
      setPersonData({
        title: '',
        nickName: '',
        firstName: '',
        lastName: '',
        firstName_eng: '',
        lastName_eng: '',
        jobTitle: '',
        department: '',
        email: '',
        phone: ''
      });
      setCompanyData({
        companyName: '',
        companyName_eng: '',
        industry: '',
        website: '',
        address: '',
        city: '',
        country: '',
        phone: '',
        branch: '',
        companyEmail: '',
        taxID: ''
      });
    }
    console.log('=== END CUSTOMER UPDATE EFFECT ===');
  }, [mode, customer, launchedFromCompanyDetail, effectivePreselectedCompany]);

     // Debug logging for selectedCompanyId changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('selectedCompanyId changed:', {
        selectedCompanyId,
        customerId: customer?.CustomerID,
        mode,
        timestamp: new Date().toISOString()
      });
    }
  }, [selectedCompanyId, customer?.CustomerID, mode]);

  // Debug logging for customer prop changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Customer prop changed:', {
        customerId: customer?.CustomerID,
        companyId: customer?.CompanyID,
        companyName: customer?.CompanyName,
        customerType: customer?.CustomerType,
        timestamp: new Date().toISOString()
      });
    }
  }, [customer]);

  // Person fields with bilingual support
  // Initialize with prefillData if available (for email hover feature)
  const [personData, setPersonData] = useState(() => {
    const initialData = {
      title: customer?.PersonTitle || '',
      nickName: customer?.NickName || '',
      firstName: customer?.FirstName || '',
      lastName: customer?.LastName || '',
      firstName_eng: customer?.FirstName_Eng || '',
      lastName_eng: customer?.LastName_Eng || '',
      jobTitle: customer?.JobTitle || '',
      department: customer?.Department || '',
      email: customer?.PersonEmail || '',
      phone: customer?.PersonPhone || ''
    };
    
    // If prefillData is available, merge it
    if (prefillData && mode === 'create') {
      console.log('Initializing personData with prefillData:', prefillData);
      return {
        title: prefillData.title || initialData.title,
        nickName: initialData.nickName,
        firstName: prefillData.firstName || initialData.firstName,
        lastName: prefillData.lastName || initialData.lastName,
        firstName_eng: prefillData.firstName_eng || initialData.firstName_eng,
        lastName_eng: prefillData.lastName_eng || initialData.lastName_eng,
        jobTitle: prefillData.jobTitle || initialData.jobTitle,
        department: prefillData.department || initialData.department,
        email: prefillData.email || initialData.email,
        phone: prefillData.phone || initialData.phone
      };
    }
    
    return initialData;
  });

  // Company fields with bilingual support
  const [companyData, setCompanyData] = useState({
    companyName: customer?.CompanyName || '',
    companyName_eng: customer?.CompanyName_Eng || '',
    industry: customer?.Industry || '',
    website: customer?.Website || '',
    address: customer?.CompanyAddress || '',
    city: customer?.City || '',
    country: customer?.Country || '',
    phone: customer?.Phone || '',
    branch: customer?.Branch || '',
    companyEmail: customer?.CompanyEmail || '',
    taxID: customer?.TaxID || ''
  });

  // Additional fields from entity config
  const [customerCode, setCustomerCode] = useState(customer?.CustomerCode || '');
  const [combinationName, setCombinationName] = useState(customer?.CombinationName || '');

  // Helper function to normalize website URL - auto-prepend https:// if missing
  const normalizeWebsite = (website) => {
    if (!website || !website.trim()) {
      return website; // Return empty string or null as-is
    }
    const trimmed = website.trim();
    // If it doesn't start with http:// or https://, prepend https://
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const validateForm = () => {
    const newErrors = {};

    console.log('Validating form with:', {
      customerType,
      personData,
      companyData,
      showNewCompany,
      selectedSalespersonId,
      salespersonsList: salespersonsList.length
    });

    if (customerType === 'Person') {
      if (isEmpty(personData.firstName)) {
        newErrors.firstName = 'กรุณากรอกชื่อ / First name is required';
        console.log('Validation error: firstName is required');
      }
      // Last name is now optional - removed validation
      if (!isEmpty(personData.email) && !isValidEmail(personData.email)) {
        newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง / Invalid email format';
        console.log('Validation error: email is invalid');
      }
      // If creating new company, require its name
      if (showNewCompany && isEmpty(companyData.companyName)) {
        newErrors.companyName = 'กรุณากรอกชื่อบริษัท / Company name is required';
        console.log('Validation error: company name is required for new company');
      }
      
      // Validate salesperson assignment
      if (selectedSalespersonId && String(selectedSalespersonId).trim() !== '') {
        const salespersonExists = salespersonsList.some(sp => String(sp.UserID) === String(selectedSalespersonId));
        if (!salespersonExists) {
          newErrors.salesperson = 'พนักงานขายที่เลือกไม่มีในระบบ / Selected salesperson is no longer available';
          console.log('Validation error: selected salesperson does not exist in current list');
          console.log('Selected salesperson ID:', selectedSalespersonId);
          console.log('Available salesperson IDs:', salespersonsList.map(sp => sp.UserID));
        }
      }
    } else {
      if (isEmpty(companyData.companyName)) {
        newErrors.companyName = 'กรุณากรอกชื่อบริษัท / Company name is required';
        console.log('Validation error: company name is required');
      }
      
      // Validate company email if provided
      if (!isEmpty(companyData.companyEmail) && !isValidEmail(companyData.companyEmail)) {
        newErrors.companyEmail = 'รูปแบบอีเมลไม่ถูกต้อง / Invalid email format';
      }
      
      // Validate salesperson assignment for company
      if (selectedSalespersonId && String(selectedSalespersonId).trim() !== '') {
        const salespersonExists = salespersonsList.some(sp => String(sp.UserID) === String(selectedSalespersonId));
        if (!salespersonExists) {
          newErrors.salesperson = 'พนักงานขายที่เลือกไม่มีในระบบ / Selected salesperson is no longer available';
          console.log('Validation error: selected salesperson does not exist in current list');
          console.log('Selected salesperson ID:', selectedSalespersonId);
          console.log('Available salesperson IDs:', salespersonsList.map(sp => sp.UserID));
        }
      }
      // Website validation removed - will auto-fill http:// if missing
    }

    console.log('Validation errors found:', newErrors);
    setErrors(newErrors);
    
    // Scroll to first error
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // Helpers for Related Entities selection
  const getEntityId = (entity, entityType) => {
    switch (entityType) {
      case 'Lead': return entity.LeadID;
      case 'Opportunity': return entity.OpportunityID;
      case 'Activity': return entity.ActivityID;
      case 'SaleNote': return entity.SaleNoteID || entity.NoteID;
      default: return null;
    }
  };

  const getEntityName = (entity, entityType) => {
    switch (entityType) {
      case 'Lead': return entity.Title || entity.ContactName || entity.CompanyName || `Lead ${entity.LeadID}`;
      case 'Opportunity': return entity.OpportunityName || entity.Title || `Opportunity ${entity.OpportunityID}`;
      case 'Activity': return entity.Subject || `${entity.ActivityType || 'Activity'} ${entity.ActivityID}`;
      case 'SaleNote': {
        const noteText = entity.NoteText || '';
        const firstLine = noteText.split('\n')[0] || noteText;
        const previewText = firstLine.length > 100 ? firstLine.substring(0, 100).trim() + '...' : firstLine.trim();
        return previewText || `Sale Note ${entity.NoteID || entity.SaleNoteID}`;
      }
      default: return `${entityType}`;
    }
  };

  const getEntitySubInfo = (entity, entityType) => {
    switch (entityType) {
      case 'Lead':
        return entity.CompanyName || entity.ContactEmail || entity.ContactPhone || '';
      case 'Opportunity':
        return entity.Stage || '';
      case 'Activity':
        return entity.ActivityType || entity.Status || '';
      case 'SaleNote':
        if (entity.NoteDate) {
          try {
            return new Date(entity.NoteDate).toLocaleDateString();
          } catch (_) {
            return '';
          }
        }
        return '';
      default:
        return '';
    }
  };

  const openEntitySelectionModal = (entityType, currentSearchTerm = '') => {
    const alreadySelectedIds = relatedEntities
      .filter(e => e.type === entityType)
      .map(e => e.id.toString());
    setEntitySelectionModal({
      isOpen: true,
      entityType,
      searchTerm: currentSearchTerm,
      results: [],
      loading: true,
      selectedIds: alreadySelectedIds
    });
    performEntitySearchForModal(currentSearchTerm || '', entityType);
  };

  const closeEntitySelectionModal = (addSelected = false) => {
    if (addSelected && entitySelectionModal.entityType) {
      const newEntities = [];
      entitySelectionModal.results.forEach(entity => {
        const id = getEntityId(entity, entitySelectionModal.entityType);
        const idStr = id?.toString();
        if (!idStr) return;
        const isSelected = entitySelectionModal.selectedIds.includes(idStr);
        const already = relatedEntities.some(e => e.type === entitySelectionModal.entityType && e.id.toString() === idStr);
        if (isSelected && !already) {
          newEntities.push({ type: entitySelectionModal.entityType, id, name: getEntityName(entity, entitySelectionModal.entityType) });
        }
      });
      const remainingIds = new Set(entitySelectionModal.selectedIds);
      const kept = relatedEntities.filter(e => e.type !== entitySelectionModal.entityType || remainingIds.has(e.id.toString()));
      setRelatedEntities([...kept, ...newEntities]);
    }
    setEntitySelectionModal({ isOpen: false, entityType: null, searchTerm: '', results: [], loading: false, selectedIds: [] });
  };

  const toggleEntitySelectionInModal = (entity, entityType) => {
    const idStr = getEntityId(entity, entityType)?.toString();
    if (!idStr) return;
    setEntitySelectionModal(prev => {
      const isSelected = prev.selectedIds.includes(idStr);
      return { ...prev, selectedIds: isSelected ? prev.selectedIds.filter(x => x !== idStr) : [...prev.selectedIds, idStr] };
    });
  };

  const handleEntityModalSearchChange = (value) => {
    setEntitySelectionModal(prev => ({ ...prev, searchTerm: value }));
    const entityType = entitySelectionModal.entityType;
    if (!entityType) return;
    const key = `modal-${entityType}`;
    if (searchTimeouts[key]) clearTimeout(searchTimeouts[key]);
    const timeoutId = setTimeout(() => performEntitySearchForModal(value, entityType), 300);
    setSearchTimeouts(prev => ({ ...prev, [key]: timeoutId }));
  };

  const performEntitySearchForModal = async (term, entityType) => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'Lead':
          endpoint = !term || term.trim() === '' ? `/api/leads/for-assignment?limit=10` : `/api/leads/for-assignment?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Opportunity':
          endpoint = !term || term.trim() === '' ? `/api/opportunities/for-assignment?limit=10` : `/api/opportunities/for-assignment?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Activity':
          endpoint = !term || term.trim() === '' ? `/api/activities?limit=10` : `/api/activities?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'SaleNote':
          endpoint = !term || term.trim() === '' ? `/api/sales-notes?limit=10` : `/api/sales-notes?search=${encodeURIComponent(term)}&limit=15`;
          break;
        default:
          return;
      }
      const response = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } });
      if (response.ok) {
        const data = await response.json();
        let results = [];
        if (data.leads) results = data.leads;
        else if (data.opportunities) results = data.opportunities;
        else if (data.activities) results = data.activities;
        else if (data.saleNotes) results = data.saleNotes;
        else if (data.salesNotes) results = data.salesNotes;
        else if (data.data && Array.isArray(data.data)) results = data.data;
        else if (Array.isArray(data)) results = data;
        setEntitySelectionModal(prev => ({ ...prev, results, loading: false }));
      } else {
        setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
      }
    } catch (err) {
      setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
    }
  };

  const handleAddCustomAttributeField = async ({ fieldName, label, type, options: choiceOptions = [] }) => {
    console.log(`📝 CustomerForm: Adding custom field "${fieldName}" (${label}, type: ${type})`);
    console.log(`   Current custom fields count: ${customAttributeFields.length}`);
    console.log(`   Tenant code: ${tenantCode}`);
    
    if (!tenantCode) {
      const errorMessage = 'Tenant context is required to add custom fields.';
      console.error(`❌ CustomerForm: ${errorMessage}`);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
    
    const orderBase = 900 + customAttributeFields.length * 5;
    const isChoiceField = type === 'choice';
    const dataType =
      type === 'number' ? 'decimal' : type === 'date' ? 'date' : 'string';
    const timestamp = new Date().toISOString();

    const isThaiLanguage = i18n?.language?.toLowerCase().startsWith('th') || false;
    if (isChoiceField && choiceOptions.length === 0) {
      const message = 'At least one option is required for choice fields.';
      console.error(`❌ CustomerForm: ${message}`);
      toast.error(message);
      throw new Error(message);
    }

    const slugifyOptionValue = (inputValue = '', fallbackIndex = 0) => {
      const normalised = inputValue
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '');

      if (normalised) return normalised;
      return `option_${fallbackIndex + 1}`;
    };

    const seenOptionValues = new Set();
    const normalizedOptions = isChoiceField
      ? choiceOptions.map((option, index) => {
          let baseValue = slugifyOptionValue(
            option?.value ?? option?.label ?? '',
            index
          );
          let uniqueValue = baseValue;
          let counter = 1;

          while (seenOptionValues.has(uniqueValue)) {
            counter += 1;
            uniqueValue = slugifyOptionValue(`${baseValue}_${counter}`, index + counter);
          }

          seenOptionValues.add(uniqueValue);

          return {
            value: uniqueValue,
            label: option?.label ?? uniqueValue,
            ...(option?.labelTH ? { labelTH: option.labelTH } : {})
          };
        })
      : [];

    const fieldConfig = {
      displayName: label,
      displayNameTH: label,
      description: `Custom attribute "${label}"`,
      dataType,
      metadata: {
        isCustomAttribute: true,
        attributeType: isChoiceField ? 'choice' : type,
        attributeSource: 'attributes',
        createdAt: timestamp,
        tenantCode,
        tenantOnly: true
      },
      storage: {
        type: 'attributes',
        path: fieldName
      },
      validation: {
        required: false,
        ...(isChoiceField
          ? { enum: normalizedOptions.map((option) => option.value) }
          : {})
      },
      ui: {
        inputType: isChoiceField ? 'select' : type,
        showInForm: true,
        showInDetail: true,
        showInList: false,
        showInExport: false,
        group: 'Customer Additional Data',
        order: orderBase,
        ...(isChoiceField ? { options: normalizedOptions } : {})
      },
      _source: 'tenant_custom',
      _source_tenant: tenantCode,
      _created_at: timestamp,
      _tenantSpecific: true,
      _tenantCode: tenantCode
    };

    console.log(`   Sending field config to backend...`);
    const response = await fetch('/api/tenant-entity-config/customer/custom-field', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      },
      body: JSON.stringify({
        fieldName,
        fieldConfig
      })
    });

    let responseBody = null;
    if (!response.ok) {
      responseBody = await response.json().catch(() => ({}));
      console.error(`❌ Failed to add custom field:`, responseBody);
      throw new Error(responseBody?.message || 'Failed to add custom field');
    } else {
      responseBody = await response.json().catch(() => ({}));
      console.log(`✅ Backend confirmed field added:`, responseBody);
    }

    console.log(`   Clearing entity config cache...`);
    clearConfigCache('customer');
    
    console.log(`   Reloading entity config from backend...`);
    const updatedConfig = await loadEntityConfig('customer', tenantCode);
    console.log(`   Updated config has ${Object.keys(updatedConfig?.fields || {}).length} total fields`);
    
    const updatedFields = extractCustomAttributeFields(
      updatedConfig || entityConfig || {},
      {
        tenantCode,
        includeGlobal: true
      }
    );
    console.log(`   Extracted ${updatedFields.length} custom attribute fields`);
    console.log(`   Custom field names:`, updatedFields.map(f => f.name));

    setCustomAttributeFields(updatedFields);
    console.log(`   Updated customAttributeFields state`);

    setAttributes((prev) => {
      const next = { ...prev };
      updatedFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = '';
        }
      });
      console.log(`   Initialized attributes for new fields:`, Object.keys(next));
      return next;
    });

    console.log(`✅ Custom field "${fieldName}" added successfully and UI updated`);
    return responseBody;
  };

  // Handle continuing with save despite duplicates
  const handleContinueWithSave = async () => {
    const formData = duplicateWarning.pendingFormData;
    setDuplicateWarning({ show: false, duplicates: [], pendingFormData: null });
    
    // Proceed with the save using the pending form data
    setFormLoading(true);
    try {
      await actualSaveLogic(formData);
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.message || safeT('errors.saveFailed', 'Failed to save customer'));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle using an existing customer instead of creating new one - show comparison first
  const handleUseExisting = (existingCustomer) => {
    const formData = duplicateWarning.pendingFormData;
    
    // Build initial field selections by comparing new vs existing
    const newPersonData = formData.person || {};
    const existingData = existingCustomer;
    
    const fields = [
      { key: 'FirstName', newKey: 'firstName' },
      { key: 'LastName', newKey: 'lastName' },
      { key: 'FirstName_Eng', newKey: 'firstName_eng' },
      { key: 'LastName_Eng', newKey: 'lastName_eng' },
      { key: 'Title', newKey: 'title', existingAlt: 'PersonTitle' },
      { key: 'Email', newKey: 'email', existingAlt: 'PersonEmail' },
      { key: 'Phone', newKey: 'phone', existingAlt: 'PersonPhone' },
      { key: 'JobTitle', newKey: 'jobTitle' },
      { key: 'Department', newKey: 'department' },
      { key: 'NickName', newKey: 'nickName' },
    ];
    
    const fieldSelections = {};
    fields.forEach(f => {
      const newVal = newPersonData[f.newKey] || '';
      const existingVal = existingData[f.key] || existingData[f.existingAlt] || '';
      
      // Default to new value if provided, otherwise existing
      if (newVal && newVal.trim()) {
        fieldSelections[f.key] = newVal.trim();
      } else if (existingVal && String(existingVal).trim()) {
        fieldSelections[f.key] = String(existingVal).trim();
      } else {
        fieldSelections[f.key] = '';
      }
    });
    
    // Close duplicate warning and show merge comparison
    setDuplicateWarning({ show: false, duplicates: [], pendingFormData: null });
    setMergeWithExisting({
      show: true,
      existingCustomer,
      pendingFormData: formData,
      fieldSelections
    });
  };

  // Execute the merge with existing customer using selected field values
  const handleConfirmMergeWithExisting = async () => {
    const { existingCustomer, fieldSelections } = mergeWithExisting;
    
    setFormLoading(true);
    try {
      console.log('Updating existing customer:', existingCustomer.CustomerID);
      console.log('With merged field selections:', fieldSelections);
      
      // Build the update payload
      const updateData = {
        customerType: 'Person',
        person: {
          firstName: fieldSelections.FirstName || null,
          lastName: fieldSelections.LastName || null,
          firstName_eng: fieldSelections.FirstName_Eng || null,
          lastName_eng: fieldSelections.LastName_Eng || null,
          title: fieldSelections.Title || null,
          email: fieldSelections.Email || null,
          phone: fieldSelections.Phone || null,
          jobTitle: fieldSelections.JobTitle || null,
          department: fieldSelections.Department || null,
          nickName: fieldSelections.NickName || null,
        }
      };
      
      // Call PUT to update the existing customer
      const response = await api.put(`/customers/${existingCustomer.CustomerID}`, updateData);
      
      toast.success(safeT('customers.updateSuccess', 'Customer updated successfully'));
      
      // Close modal and navigate to the updated customer
      setMergeWithExisting({ show: false, existingCustomer: null, pendingFormData: null, fieldSelections: {} });
      navigate(`/customers/${existingCustomer.CustomerID}`);
    } catch (error) {
      console.error('Update existing customer failed:', error);
      toast.error(error.response?.data?.message || safeT('errors.updateFailed', 'Failed to update customer'));
    } finally {
      setFormLoading(false);
    }
  };

  // Update a field selection in merge with existing modal
  const handleMergeFieldChange = (fieldKey, value) => {
    setMergeWithExisting(prev => ({
      ...prev,
      fieldSelections: {
        ...prev.fieldSelections,
        [fieldKey]: value
      }
    }));
  };

  // Extracted save logic that can be reused
  const actualSaveLogic = async (formData) => {
    console.log('Starting form submission...');
    console.log('=== FORM SUBMISSION DEBUG ===');
    console.log('Mode:', mode);
    console.log('Customer Type:', customerType);
    console.log('Final Form Data:', formData);
    console.log('=== END FORM SUBMISSION DEBUG ===');
     
    if (process.env.NODE_ENV === 'development') {
      console.log('Form submission data:', {
        mode,
        customerType,
        formData
      });
    }

    if (onSubmit) {
      console.log('Using onSubmit prop');
      const submitResult = await onSubmit(formData);
      // After save via prop, try to sync relationships
      let customerId = customer?.CustomerID || id;
      if (!customerId && submitResult) {
        customerId = submitResult?.customer?.CustomerID || submitResult?.CustomerID || submitResult?.id || submitResult?.customerId;
      }
      return submitResult;
    } else {
      // Direct API call
      let response;
      if (mode === 'create') {
        response = await api.post('/customers', formData);
      } else {
        response = await api.put(`/customers/${customer.CustomerID}`, formData);
      }

      toast.success(mode === 'create' 
        ? safeT('customers.createSuccess', 'Customer created successfully')
        : safeT('customers.updateSuccess', 'Customer updated successfully')
      );

      if (mode === 'create' && response.customer) {
        navigate(`/customers/${response.customer.CustomerID}`);
      } else {
        if (onCancel) {
          onCancel();
        } else {
          navigate('/customers');
        }
      }
      
      return response;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug validation
    console.log('🔍 Form submission started');
    console.log('🔍 Form data:', {
      customerType,
      personData,
      companyData,
      selectedCompanyId,
      showNewCompany,
      selectedSalespersonId
    });
    
    const isValid = validateForm();
    console.log('🔍 Form validation result:', isValid);
    console.log('🔍 Current errors:', errors);
    
    if (!isValid) {
      console.log('🔍 Form validation failed, not submitting');
      console.log('🔍 Validation errors:', errors);
      return;
    }

    console.log('🔍 Validation passed, setting loading state');

    const normalizedPersonData = {
      ...personData,
      email: normalizeEmailList(personData.email)
    };
    const normalizedCompanyData = {
      ...companyData,
      companyEmail: normalizeEmailList(companyData.companyEmail)
    };

    // Check for duplicate persons by email before saving
    if (customerType === 'Person' && normalizedPersonData.email && normalizedPersonData.email.trim()) {
      try {
        const duplicateCheck = await api.post('/customers/check-email-duplicates', {
          email: normalizedPersonData.email,
          excludeCustomerId: customer?.CustomerID
        });
        
        if (duplicateCheck.hasDuplicates && duplicateCheck.duplicates.length > 0) {
          // Prepare the form data to pass to the warning modal
          const sanitizedAttributes = sanitizeAttributePayload(
            attributes,
            customAttributeFields
          );

          const formData = {
            customerType,
            attributes: sanitizedAttributes,
            customerSummary: customerSummary ? customerSummary.trim() : ''
          };
          
          if (customerCode && customerCode.trim()) {
            formData.customerCode = customerCode.trim();
          }
          if (combinationName && combinationName.trim()) {
            formData.combinationName = combinationName.trim();
          }
          if (selectedSalespersonId) {
            formData.salespersonId = selectedSalespersonId;
          }

          const dynamicFields = getDynamicFormFields();
          dynamicFields.forEach(field => {
            const fieldName = field.name;
            const dbTable = field.database?.table;
            if (dbTable === 'Customers' || !dbTable) {
              const value = customer?.[fieldName];
              if (value !== undefined && value !== null && value !== '') {
                formData[fieldName] = value;
              }
            }
          });
          
          formData.person = normalizedPersonData;
          
          if (showNewCompany) {
            formData.newCompany = {
              name: companyData.companyName,
              name_eng: companyData.companyName_eng,
              industry: companyData.industry,
              address: companyData.address,
              city: companyData.city,
              country: companyData.country,
              phone: companyData.phone,
              website: normalizeWebsite(companyData.website),
              branch: companyData.branch,
              companyEmail: normalizedCompanyData.companyEmail,
              taxID: companyData.taxID
            };
          } else if (selectedCompanyId && String(selectedCompanyId).trim() !== '') {
            formData.companyId = selectedCompanyId;
          }

          // Show duplicate warning dialog
          setDuplicateWarning({
            show: true,
            duplicates: duplicateCheck.duplicates,
            pendingFormData: formData
          });
          return; // Don't proceed with save yet
        }
      } catch (error) {
        console.error('Failed to check for duplicates:', error);
        // Continue with save even if duplicate check fails
      }
    }

    // Check if we need to show confirmation dialog for saving company as customer
    if (mode === 'create' && customerType === 'Person' && showNewCompany && companyData.companyName.trim()) {
      // Prepare form data first
      const sanitizedAttributes = sanitizeAttributePayload(
        attributes,
        customAttributeFields
      );

      const formData = {
        customerType,
        attributes: sanitizedAttributes
      };
      
      formData.customerSummary = customerSummary ? customerSummary.trim() : '';
      
      if (customerCode && customerCode.trim()) {
        formData.customerCode = customerCode.trim();
      }
      if (combinationName && combinationName.trim()) {
        formData.combinationName = combinationName.trim();
      }
      
      if (selectedSalespersonId) {
        formData.salespersonId = selectedSalespersonId;
      }

      const dynamicFields = getDynamicFormFields();
      dynamicFields.forEach(field => {
        const fieldName = field.name;
        const dbTable = field.database?.table;
        if (dbTable === 'Customers' || !dbTable) {
          const value = customer?.[fieldName];
          if (value !== undefined && value !== null && value !== '') {
            formData[fieldName] = value;
          }
        }
      });
      
      formData.person = normalizedPersonData;
      formData.newCompany = {
        name: companyData.companyName,
        name_eng: companyData.companyName_eng,
        industry: companyData.industry,
        address: companyData.address,
        city: companyData.city,
        country: companyData.country,
        phone: companyData.phone,
        website: normalizeWebsite(companyData.website),
        branch: companyData.branch,
        companyEmail: normalizedCompanyData.companyEmail,
        taxID: companyData.taxID
      };
      
      // Show confirmation dialog
      setSaveCompanyDialog({
        isOpen: true,
        pendingFormData: formData
      });
      return;
    }

    // Continue with normal submission
    setFormLoading(true);

    try {
      console.log('Starting form submission...');
      // Prepare payload
    const sanitizedAttributes = sanitizeAttributePayload(
      attributes,
      customAttributeFields
    );

    const formData = {
      customerType,
      attributes: sanitizedAttributes
    };
      
      // Always include CustomerSummary (even if empty) to allow clearing it
      formData.customerSummary = customerSummary ? customerSummary.trim() : '';
      
      // Add CustomerCode and CombinationName if provided
      if (customerCode && customerCode.trim()) {
        formData.customerCode = customerCode.trim();
      }
      if (combinationName && combinationName.trim()) {
        formData.combinationName = combinationName.trim();
      }
      
      // Add salesperson ID if selected
      if (selectedSalespersonId) {
        formData.salespersonId = selectedSalespersonId;
      }

      // Include dynamic fields from entity config (fields in Customers table)
      const dynamicFields = getDynamicFormFields();
      dynamicFields.forEach(field => {
        const fieldName = field.name;
        const dbTable = field.database?.table;
        // Only include fields from Customers table (not Persons or Companies, those are handled separately)
        if (dbTable === 'Customers' || !dbTable) {
          const value = customer?.[fieldName];
          if (value !== undefined && value !== null && value !== '') {
            formData[fieldName] = value;
          }
        }
      });
      
             if (customerType === 'Person') {
        formData.person = normalizedPersonData;
         if (showNewCompany) {
          formData.newCompany = {
            name: companyData.companyName,
            name_eng: companyData.companyName_eng,
            industry: companyData.industry,
            address: companyData.address,
            city: companyData.city,
            country: companyData.country,
            phone: companyData.phone,
            website: normalizeWebsite(companyData.website),
            branch: companyData.branch,
            companyEmail: normalizedCompanyData.companyEmail,
            taxID: companyData.taxID
          };
         } else if (selectedCompanyId && String(selectedCompanyId).trim() !== '') {
           formData.companyId = selectedCompanyId;
         } else {
           // Explicitly set companyId to null when no company is selected
           formData.companyId = null;
         }
         // Always include salesperson assignment for persons
         if (selectedSalespersonId) {
           formData.salespersonId = selectedSalespersonId;
         }
       } else {
        formData.company = {
          name: companyData.companyName,
          name_eng: companyData.companyName_eng,
          industry: companyData.industry,
          address: companyData.address,
          city: companyData.city,
          country: companyData.country,
          phone: companyData.phone,
          website: normalizeWebsite(companyData.website),
          branch: companyData.branch,
          companyEmail: normalizedCompanyData.companyEmail,
          taxID: companyData.taxID
        };
        // Include salesperson assignment for companies
        if (selectedSalespersonId) {
          formData.salespersonId = selectedSalespersonId;
        }
      }

             // Debug logging for form submission
      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('Mode:', mode);
      console.log('Customer Type:', customerType);
      console.log('Selected Company ID:', selectedCompanyId);
      console.log('Selected Company ID Type:', typeof selectedCompanyId);
      console.log('Selected Company ID Length:', selectedCompanyId ? String(selectedCompanyId).length : 'N/A');
      console.log('Show New Company:', showNewCompany);
      console.log('Selected Salesperson ID:', selectedSalespersonId);
      console.log('Person Data:', personData);
      console.log('Company Data:', companyData);
      console.log('Final Form Data:', formData);
      console.log('Company ID in Form Data:', formData.companyId);
      console.log('Company ID Type in Form Data:', typeof formData.companyId);
      
      // Debug Thai characters specifically
      if (personData.lastName && /[^\x00-\x7F]/.test(personData.lastName)) {
        console.log('🔍 Thai lastName detected in form:', {
          lastName: personData.lastName,
          length: personData.lastName.length,
          bytes: new TextEncoder().encode(personData.lastName).length,
          charCodes: personData.lastName.split('').map(c => c.charCodeAt(0)),
          hex: Array.from(new TextEncoder().encode(personData.lastName)).map(b => b.toString(16)).join('')
        });
      }
      
      console.log('=== END FORM SUBMISSION DEBUG ===');
       
       if (process.env.NODE_ENV === 'development') {
         console.log('Form submission data:', {
           mode,
           customerType,
           formData,
           selectedCompanyId,
           showNewCompany,
           selectedSalespersonId
         });
       }

      if (onSubmit) {
        console.log('Using onSubmit prop');
        const submitResult = await onSubmit(formData);
        // After save via prop, try to sync relationships
        let customerId = customer?.CustomerID || id;
        if (!customerId && submitResult) {
          customerId = submitResult?.customer?.CustomerID || submitResult?.CustomerID || submitResult?.id || submitResult?.customerId;
        }
        if (customerId) {
          try {
            const existingKeys = new Set(initialRelationKeysRef.current ? Array.from(initialRelationKeysRef.current) : []);
            const selectedKeys = new Set(relatedEntities.map(e => `${e.type}:${e.id}`));
            const toCreate = relatedEntities.filter(e => !existingKeys.has(`${e.type}:${e.id}`));
            let deletePromises = [];
            if (initialRelationKeysRef.current && initialRelationKeysRef.current.size > 0) {
              try {
                const existing = await entityRelationService.getRelatedEntities('Customer', Number(customerId), { direction: 'both' });
                const relations = existing.relations || [];
                const relationsToDelete = relations.filter(rel => {
                  const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
                  const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
                  const srcId = rel.SourceEntityID || rel.sourceEntityID;
                  const tgtId = rel.TargetEntityID || rel.targetEntityID;
                  const isCustomerSource = (srcType || '').toString().toLowerCase() === 'customer';
                  const entityType = isCustomerSource ? tgtType : srcType;
                  const entityId = isCustomerSource ? tgtId : srcId;
                  const key = `${entityType}:${entityId}`;
                  return !selectedKeys.has(key);
                });
                if (relationsToDelete.length > 0) {
                  const relationIDsToDelete = relationsToDelete.map(rel => rel.RelationID || rel.relationID).filter(Boolean);
                  if (relationIDsToDelete.length > 0) {
                    deletePromises = [entityRelationService.deleteBatch(relationIDsToDelete, 5000)];
                  }
                }
              } catch (e) {
                console.warn('CustomerForm (prop submit): Failed to refetch relations for deletions', e);
              }
            }
            const createPromises = toCreate.map(e =>
              entityRelationService.create({
                sourceEntityType: 'Customer',
                sourceEntityID: Number(customerId),
                targetEntityType: e.type,
                targetEntityID: e.id,
                relationType: 'related_to'
              })
            );
            await Promise.allSettled(createPromises);
            if (deletePromises.length > 0) await Promise.allSettled(deletePromises);
          } catch (relErr) {
            console.error('❌ CustomerForm (prop submit): Relationship sync failed with exception:', relErr);
          }
        }
      } else {
        // Handle submission when accessed via URL - use performSubmission
        await performSubmission(formData, false);
      }
    } catch (error) {
      console.error('🔍 Form submission error:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setErrors({ submit: error.message });
      toast.error(error.message || 'Failed to save customer');
    } finally {
      console.log('🔍 Form submission completed, setting loading to false');
      setFormLoading(false);
    }
  };

  // Handle actual form submission (with or without creating company as customer)
  const performSubmission = async (formData, createCompanyAsCustomer = false) => {
    setFormLoading(true);
    
    try {
      if (createCompanyAsCustomer && customerType === 'Person' && showNewCompany) {
        // Step 1: Create Company customer first
        const companyFormData = {
          customerType: 'Company',
          company: {
            name: formData.newCompany.name,
            name_eng: formData.newCompany.name_eng,
            industry: formData.newCompany.industry,
            address: formData.newCompany.address,
            city: formData.newCompany.city,
            country: formData.newCompany.country,
            phone: formData.newCompany.phone,
            website: formData.newCompany.website,
            branch: formData.newCompany.branch,
            companyEmail: formData.newCompany.companyEmail,
            taxID: formData.newCompany.taxID
          },
          attributes: {},
          customerSummary: ''
        };
        
        // Include salesperson if selected
        if (formData.salespersonId) {
          companyFormData.salespersonId = formData.salespersonId;
        }
        
        console.log('Creating Company customer first...', companyFormData);
        const selectedRole = localStorage.getItem('selectedRole');
        const companyResponse = await fetch('/api/customers', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          },
          body: JSON.stringify(companyFormData)
        });
        
        if (!companyResponse.ok) {
          const errorData = await companyResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to create company customer');
        }
        
        const companyResult = await companyResponse.json();
        const createdCustomerId = companyResult?.customerId || companyResult?.id || companyResult?.CustomerID || companyResult?.customer?.CustomerID;
        
        if (!createdCustomerId) {
          console.error('Failed to get CustomerID from created company customer. Full response:', companyResult);
          throw new Error('Failed to get CustomerID from created company customer. The company may not have been created properly.');
        }
        
        console.log('Company customer created successfully with CustomerID:', createdCustomerId);
        toast.success(safeT('customers.companyCreatedSuccess', 'Company customer created successfully!'));
        
        // Fetch the created company customer to get the CompanyID from Companies table
        // The backend response doesn't include CompanyID, so we need to fetch it
        console.log('Fetching created company customer to get CompanyID...');
        const fetchCompanyResponse = await fetch(`/api/customers/${createdCustomerId}`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        
        if (!fetchCompanyResponse.ok) {
          throw new Error('Failed to fetch created company customer to get CompanyID');
        }
        
        const fetchedCompanyData = await fetchCompanyResponse.json();
        const createdCompany = fetchedCompanyData?.customer || fetchedCompanyData;
        const createdCompanyId = createdCompany?.CompanyID || createdCompany?.companyID;
        
        console.log('Fetched company customer data:', {
          CustomerID: createdCustomerId,
          CompanyID: createdCompanyId,
          fullData: createdCompany
        });
        
        if (!createdCompanyId) {
          console.error('Failed to get CompanyID from fetched company customer. Full data:', createdCompany);
          throw new Error('Failed to get CompanyID from created company customer. The company may not have been created properly.');
        }
        
        // Step 2: Create Person customer associated with the created company
        // Use CompanyID (not CustomerID) for the foreign key constraint
        const personFormData = {
          ...formData,
          companyId: String(createdCompanyId), // Use CompanyID from Companies table
          newCompany: undefined // Remove newCompany since we're using existing company
        };
        
        console.log('Creating Person customer associated with company...', {
          personFormData,
          usingCompanyId: personFormData.companyId,
          createdCompanyId,
          createdCustomerId
        });
        const personResponse = await fetch('/api/customers', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          },
          body: JSON.stringify(personFormData)
        });
        
        if (!personResponse.ok) {
          const errorData = await personResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to create person customer');
        }
        
        const personResult = await personResponse.json();
        const createdPersonId = personResult?.customer?.CustomerID || personResult?.CustomerID || personResult?.id || personResult?.customerId;
        
        console.log('Person customer created successfully:', createdPersonId);
        toast.success(safeT('customers.personCreatedSuccess', 'Person customer created successfully!'));
        
        // Navigate to the person customer detail page
        if (createdPersonId) {
          navigate(`/customers/${createdPersonId}`, { replace: true, state: { forceRefresh: Date.now() } });
        } else {
          navigate('/customers');
        }
      } else {
        // Normal submission flow (without creating company as customer)
        const url = mode === 'edit' ? `/api/customers/${id}` : '/api/customers';
        const method = mode === 'edit' ? 'PUT' : 'POST';
        const selectedRole = localStorage.getItem('selectedRole');
        
        console.log('🔍 Making API call:', { url, method, formData });
        
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formData)
        });
        
        console.log('🔍 API response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          let errorMessage = 'Failed to save customer';
          let errorDetails = '';
          
          // Extract detailed error information
          if (errorData.error) {
            errorDetails = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
          } else if (errorData.details) {
            errorDetails = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details);
          }
          
          // Handle specific HTTP status codes
          switch (response.status) {
            case 400:
              errorMessage = errorData.message || errorDetails || 'Validation error - please check your input';
              break;
            case 401:
              errorMessage = 'Unauthorized - please log in again';
              break;
            case 403:
              errorMessage = errorData.message || 'You do not have permission to perform this action';
              break;
            case 404:
              errorMessage = errorData.message || `Customer not found or invalid customer ID`;
              if (errorDetails) errorMessage += `: ${errorDetails}`;
              break;
            case 409:
              errorMessage = errorData.message || 'Conflict - this customer may already exist';
              break;
            case 422:
              errorMessage = errorData.message || errorDetails || 'Invalid data provided';
              break;
            case 500:
              errorMessage = errorData.message || errorDetails || 'Server error occurred - please try again or contact support';
              break;
            case 502:
            case 503:
            case 504:
              errorMessage = 'Server is temporarily unavailable - please try again later';
              break;
            default:
              if (errorData.message) {
                errorMessage = errorData.message;
                if (errorDetails && errorDetails !== errorData.message) {
                  errorMessage += `: ${errorDetails}`;
                }
              } else if (errorDetails) {
                errorMessage = `Failed to save customer: ${errorDetails}`;
              } else {
                errorMessage = `Failed to save customer (Error ${response.status}: ${response.statusText})`;
              }
          }
          
          console.error('Form submission failed:', {
            status: response.status,
            statusText: response.statusText,
            errorData,
            errorMessage,
            errorDetails,
            url: response.url
          });
          
          throw new Error(errorMessage);
        }

        const result = await response.json();
        console.log('API response received:', result);
        
        // Show success message
        if (mode === 'edit') {
          console.log('Customer updated successfully');
          toast.success('Customer updated successfully!');
        } else {
          console.log('Customer created successfully');
          toast.success('Customer created successfully!');
        }

        // After save, sync relationships if CustomerID is available
        let customerId = customer?.CustomerID || id;
        if (!customerId && result) {
          customerId = result?.customer?.CustomerID || result?.CustomerID || result?.id || result?.customerId;
        }
        if (customerId) {
          try {
            const existingKeys = new Set(initialRelationKeysRef.current ? Array.from(initialRelationKeysRef.current) : []);
            const selectedKeys = new Set(relatedEntities.map(e => `${e.type}:${e.id}`));
            const toCreate = relatedEntities.filter(e => !existingKeys.has(`${e.type}:${e.id}`));
            let deletePromises = [];
            if (initialRelationKeysRef.current && initialRelationKeysRef.current.size > 0) {
              try {
                const existing = await entityRelationService.getRelatedEntities('Customer', Number(customerId), { direction: 'both' });
                const relations = existing.relations || [];
                const relationsToDelete = relations.filter(rel => {
                  const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
                  const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
                  const srcId = rel.SourceEntityID || rel.sourceEntityID;
                  const tgtId = rel.TargetEntityID || rel.targetEntityID;
                  const isCustomerSource = (srcType || '').toString().toLowerCase() === 'customer';
                  const entityType = isCustomerSource ? tgtType : srcType;
                  const entityId = isCustomerSource ? tgtId : srcId;
                  const key = `${entityType}:${entityId}`;
                  return !selectedKeys.has(key);
                });
                if (relationsToDelete.length > 0) {
                  const relationIDsToDelete = relationsToDelete.map(rel => rel.RelationID || rel.relationID).filter(Boolean);
                  if (relationIDsToDelete.length > 0) {
                    deletePromises = [entityRelationService.deleteBatch(relationIDsToDelete, 5000)];
                  }
                }
              } catch (e) {
                console.warn('CustomerForm: Failed to refetch relations for deletions', e);
              }
            }
            const createPromises = toCreate.map(e =>
              entityRelationService.create({
                sourceEntityType: 'Customer',
                sourceEntityID: Number(customerId),
                targetEntityType: e.type,
                targetEntityID: e.id,
                relationType: 'related_to'
              })
            );
            await Promise.allSettled(createPromises);
            if (deletePromises.length > 0) await Promise.allSettled(deletePromises);
          } catch (relErr) {
            console.error('❌ CustomerForm: Relationship sync failed with exception:', relErr);
          }
        }
        
        // Navigate to customer detail page after successful update in edit mode
        // Navigate to list page after creating new customer
        if (mode === 'edit' && customerId) {
          console.log('Navigating to customer detail page:', customerId);
          navigate(`/customers/${customerId}`, { replace: true, state: { forceRefresh: Date.now() } });
        } else {
          console.log('Navigating back to customers list');
          navigate('/customers');
        }
      }
    } catch (error) {
      console.error('🔍 Form submission error:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setErrors({ submit: error.message });
      toast.error(error.message || 'Failed to save customer');
    } finally {
      console.log('🔍 Form submission completed, setting loading to false');
      setFormLoading(false);
    }
  };

  // Handle confirmation dialog actions
  const handleSaveCompanyConfirm = async () => {
    const formDataToSubmit = saveCompanyDialog.pendingFormData;
    setSaveCompanyDialog({ isOpen: false, pendingFormData: null });
    if (formDataToSubmit) {
      await performSubmission(formDataToSubmit, true);
    }
  };

  const handleSaveCompanyCancel = async () => {
    const formDataToSubmit = saveCompanyDialog.pendingFormData;
    setSaveCompanyDialog({ isOpen: false, pendingFormData: null });
    if (formDataToSubmit) {
      // Proceed with normal submission (just create person with company association)
      await performSubmission(formDataToSubmit, false);
    }
  };

  const handlePersonChange = (field, value) => {
    setPersonData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Check if enrichment can be enabled based on required fields
  const canEnableEnrichment = () => {
    // Determine actual customer type from customer data or state
    const actualCustomerType = customer?.CustomerType || customerType;
    
    // For create mode: check form data only
    if (mode === 'create') {
      // For Person type: require (firstName + lastName) OR (firstName + companyName)
      if (actualCustomerType === 'Person') {
        const hasFirstName = personData.firstName && personData.firstName.trim().length > 0;
        const hasLastName = personData.lastName && personData.lastName.trim().length > 0;
        
        // Get company name from selected company or new company form data
        const selectedCompany = companiesList.find(c => String(c.CompanyID) === String(selectedCompanyId));
        const hasCompanyName = (selectedCompany && selectedCompany.CompanyName) ||
                              (companyData.companyName && companyData.companyName.trim().length > 0);
        
        return hasFirstName && (hasLastName || hasCompanyName);
      }
      
      // For Company type: require companyName
      if (actualCustomerType === 'Company') {
        const hasCompanyName = companyData.companyName && companyData.companyName.trim().length > 0;
        return hasCompanyName;
      }
      
      return false;
    }
    
    // For edit mode: check both form data and customer data
    if (mode === 'edit' && customer?.CustomerID) {
      // For Person type: require (firstName + lastName) OR (firstName + companyName)
      if (actualCustomerType === 'Person') {
        const hasFirstName = (personData.firstName && personData.firstName.trim().length > 0) || 
                            (customer?.FirstName && customer.FirstName.trim().length > 0);
        const hasLastName = (personData.lastName && personData.lastName.trim().length > 0) || 
                           (customer?.LastName && customer.LastName.trim().length > 0);
        
        // Get company name from selected company or new company form data
        const selectedCompany = companiesList.find(c => String(c.CompanyID) === String(selectedCompanyId));
        const hasCompanyName = (selectedCompany && selectedCompany.CompanyName) ||
                              (companyData.companyName && companyData.companyName.trim().length > 0) ||
                              (customer?.CompanyName && customer.CompanyName.trim().length > 0);
        
        return hasFirstName && (hasLastName || hasCompanyName);
      }
      
      // For Company type: require companyName (check form data first, then customer data as fallback)
      if (actualCustomerType === 'Company') {
        const hasCompanyName = (companyData.companyName && companyData.companyName.trim().length > 0) || 
                              (customer?.CompanyName && customer.CompanyName.trim().length > 0);
        return hasCompanyName;
      }
      
      // Fallback: if customer type is not determined, try to infer from customer data
      if (customer?.CompanyID && !customer?.PersonID) {
        // Has CompanyID but no PersonID, likely a Company
        const hasCompanyName = (companyData.companyName && companyData.companyName.trim().length > 0) || 
                              (customer?.CompanyName && customer.CompanyName.trim().length > 0);
        return hasCompanyName;
      } else if (customer?.PersonID && !customer?.CompanyID) {
        // Has PersonID but no CompanyID, likely a Person
        const hasFirstName = (personData.firstName && personData.firstName.trim().length > 0) || 
                            (customer?.FirstName && customer.FirstName.trim().length > 0);
        const hasLastName = (personData.lastName && personData.lastName.trim().length > 0) || 
                           (customer?.LastName && customer.LastName.trim().length > 0);
        const hasCompanyName = (companyData.companyName && companyData.companyName.trim().length > 0) ||
                              (customer?.CompanyName && customer.CompanyName.trim().length > 0);
        return hasFirstName && (hasLastName || hasCompanyName);
      }
    }
    
    return false;
  };

  // Enrich customer data using AI and search
  const handleEnrichData = async () => {
    let customerIdToEnrich = customer?.CustomerID;
    
    // For create mode: validate and create customer first
    if (mode === 'create') {
      // Validate required fields before enrichment
      if (customerType === 'Person') {
        const hasFirstName = personData.firstName && personData.firstName.trim().length > 0;
        const hasLastName = personData.lastName && personData.lastName.trim().length > 0;
        
        // Get company name from selected company or new company form data
        const selectedCompany = companiesList.find(c => String(c.CompanyID) === String(selectedCompanyId));
        const hasCompanyName = (selectedCompany && selectedCompany.CompanyName) ||
                              (companyData.companyName && companyData.companyName.trim().length > 0);
        
        if (!hasFirstName) {
          toast.error('Please fill in First Name before using enrichment');
          return;
        }
        if (!hasLastName && !hasCompanyName) {
          toast.error('Please fill in either Last Name or Company Name before using enrichment');
          return;
        }
      } else if (customerType === 'Company') {
        const hasCompanyName = companyData.companyName && companyData.companyName.trim().length > 0;
        if (!hasCompanyName) {
          toast.error('Please fill in Company Name before using enrichment');
          return;
        }
      }
      
      const normalizedPersonData = {
        ...personData,
        email: normalizeEmailList(personData.email)
      };
      const normalizedCompanyData = {
        ...companyData,
        companyEmail: normalizeEmailList(companyData.companyEmail)
      };

      // Create customer first to get ID for enrichment
      setEnriching(true);
      try {
        const sanitizedAttributes = sanitizeAttributePayload(attributes, customAttributeFields);
        const formData = {
          customerType,
          attributes: sanitizedAttributes,
          customerSummary: customerSummary ? customerSummary.trim() : '',
        };
        
        if (customerCode && customerCode.trim()) {
          formData.customerCode = customerCode.trim();
        }
        if (combinationName && combinationName.trim()) {
          formData.combinationName = combinationName.trim();
        }
        
        if (selectedSalespersonId) {
          formData.salespersonId = selectedSalespersonId;
        }
        
        if (customerType === 'Person') {
          formData.person = normalizedPersonData;
          if (showNewCompany) {
            formData.newCompany = {
              name: companyData.companyName,
              name_eng: companyData.companyName_eng,
              industry: companyData.industry,
              address: companyData.address,
              city: companyData.city,
              country: companyData.country,
              taxID: companyData.taxID,
              phone: companyData.phone,
              website: normalizeWebsite(companyData.website),
              branch: companyData.branch,
              companyEmail: normalizedCompanyData.companyEmail
            };
          } else if (selectedCompanyId && String(selectedCompanyId).trim() !== '') {
            formData.companyId = selectedCompanyId;
          } else {
            formData.companyId = null;
          }
        } else {
          formData.company = {
            name: companyData.companyName,
            name_eng: companyData.companyName_eng,
            industry: companyData.industry,
            address: companyData.address,
            city: companyData.city,
            country: companyData.country,
            taxID: companyData.taxID,
            phone: companyData.phone,
            website: normalizeWebsite(companyData.website),
            branch: companyData.branch,
            companyEmail: normalizedCompanyData.companyEmail
          };
        }
        
        // Create customer
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/customers', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to create customer for enrichment');
        }
        
        const result = await response.json();
        customerIdToEnrich = result?.customer?.CustomerID || result?.CustomerID || result?.id || result?.customerId;
        
        if (!customerIdToEnrich) {
          throw new Error('Failed to get customer ID after creation');
        }
        
        // Update local customer state with created customer
        const createdCustomer = result.customer || result;
        setCustomer(createdCustomer);
        
        // Navigate to edit mode so subsequent saves will update instead of create
        const createdCustomerId = createdCustomer.CustomerID || createdCustomer.id || createdCustomer.customerId;
        if (createdCustomerId) {
          navigate(`/customers/${createdCustomerId}/edit`, { replace: true });
        }
        
        toast.success('Customer created. Starting enrichment...');
      } catch (error) {
        console.error('Error creating customer for enrichment:', error);
        toast.error(`Failed to create customer: ${error.message}`);
        setEnriching(false);
        return;
      }
    } else {
      // Edit mode: validate required fields
      if (!customer?.CustomerID) {
        toast.error('Enrichment requires a valid customer');
        return;
      }
      
      // Determine actual customer type from customer data or state
      const actualCustomerType = customer?.CustomerType || customerType;
      
      // Validate required fields before enrichment (check form data first, then customer data as fallback)
      if (actualCustomerType === 'Person') {
        const hasFirstName = (personData.firstName && personData.firstName.trim().length > 0) || 
                            (customer?.FirstName && customer.FirstName.trim().length > 0);
        const hasLastName = (personData.lastName && personData.lastName.trim().length > 0) || 
                           (customer?.LastName && customer.LastName.trim().length > 0);
        
        // Get company name from selected company or new company form data
        const selectedCompany = companiesList.find(c => String(c.CompanyID) === String(selectedCompanyId));
        const hasCompanyName = (selectedCompany && selectedCompany.CompanyName) ||
                              (companyData.companyName && companyData.companyName.trim().length > 0) ||
                              (customer?.CompanyName && customer.CompanyName.trim().length > 0);
        
        if (!hasFirstName) {
          toast.error('Please fill in First Name before using enrichment');
          return;
        }
        if (!hasLastName && !hasCompanyName) {
          toast.error('Please fill in either Last Name or Company Name before using enrichment');
          return;
        }
      } else if (actualCustomerType === 'Company') {
        const hasCompanyName = (companyData.companyName && companyData.companyName.trim().length > 0) || 
                              (customer?.CompanyName && customer.CompanyName.trim().length > 0);
        if (!hasCompanyName) {
          toast.error('Please fill in Company Name before using enrichment');
          return;
        }
      } else {
        // Fallback: if customer type is not determined, try to infer from customer data
        if (customer?.CompanyID && !customer?.PersonID) {
          // Has CompanyID but no PersonID, likely a Company
          const hasCompanyName = (companyData.companyName && companyData.companyName.trim().length > 0) || 
                                (customer?.CompanyName && customer.CompanyName.trim().length > 0);
          if (!hasCompanyName) {
            toast.error('Please fill in Company Name before using enrichment');
            return;
          }
        } else if (customer?.PersonID && !customer?.CompanyID) {
          // Has PersonID but no CompanyID, likely a Person
          const hasFirstName = (personData.firstName && personData.firstName.trim().length > 0) || 
                              (customer?.FirstName && customer.FirstName.trim().length > 0);
          if (!hasFirstName) {
            toast.error('Please fill in First Name before using enrichment');
            return;
          }
        } else {
          toast.error('Unable to determine customer type for enrichment');
          return;
        }
      }
      
      setEnriching(true);
    }

    try {
      // Prefer Thai content when customer's name appears to be Thai
      const thaiRegex = /[\u0E00-\u0E7F]/;
      const firstName = personData.firstName || customer?.FirstName || '';
      const lastName = personData.lastName || customer?.LastName || '';
      const companyName = companyData.companyName || customer?.CompanyName || customer?.PersonCompanyName || '';
      const customerName = customer?.CustomerName || '';
      
      const thaiNamePresent = (
        (firstName && thaiRegex.test(firstName)) ||
        (lastName && thaiRegex.test(lastName)) ||
        (customerName && thaiRegex.test(customerName)) ||
        (companyName && thaiRegex.test(companyName))
      );

      // Determine customer type for enrichment options
      const actualCustomerType = customer?.CustomerType || customerType;
      const result = await customerEnrish(customerIdToEnrich, { 
        languagePreference: thaiNamePresent ? 'th' : undefined,
        customerType: actualCustomerType
      });
      
      // Debug logging
      console.log('🔍 Enrichment response:', {
        hasEnrichedData: !!result.enrichedData,
        enrichedData: result.enrichedData,
        blankFields: result.blankFields,
        searchTerms: result.searchTerms,
        sources: result.sources
      });
      
      if (result.enrichedData) {
        const items = [];
        const mapApplyToFieldName = (apply) => {
          if (!apply) return null;
          if (apply.type === 'person') {
            switch (apply.key) {
              case 'firstName': return 'FirstName';
              case 'lastName': return 'LastName';
              case 'firstName_eng': return 'FirstName_Eng';
              case 'lastName_eng': return 'LastName_Eng';
              case 'title': return 'PersonTitle';
              case 'email': return 'PersonEmail';
              case 'phone': return 'PersonPhone';
              default: return null;
            }
          }
          if (apply.type === 'company') {
            switch (apply.key) {
              case 'companyName': return 'CompanyName';
              case 'companyName_eng': return 'CompanyName_Eng';
              case 'industry': return 'Industry';
              case 'website': return 'Website';
              case 'address': return 'CompanyAddress';
              case 'city': return 'City';
              case 'country': return 'Country';
              case 'phone': return 'CompanyPhone';
              case 'branch': return 'Branch';
              case 'companyEmail': return 'CompanyEmail';
              case 'taxID': return 'TaxID';
              default: return null;
            }
          }
          return null;
        };
        const pushItem = (category, label, apply, value, originalValue) => {
          if (!value) return;
          const valStr = String(value);
          const origStr = originalValue == null ? '' : String(originalValue);
          const differs = valStr.trim() !== origStr.trim();
          if (origStr && !differs) return;
          const fieldName = mapApplyToFieldName(apply);
          if (fieldName && isFieldHidden(fieldName)) return;
          items.push({ id: `${category}-${apply.type}-${apply.key}`, category, label, apply, value: valStr, originalValue: origStr, differs, selected: true });
        };
        
        // Apply enriched data to form and track filled fields
        if (result.enrichedData.person) {
          const person = result.enrichedData.person;
          // Handle Thai fields first (for Thai names)
          pushItem('Person', 'First Name (Thai)', { type: 'person', key: 'firstName' }, person.firstName, personData.firstName);
          pushItem('Person', 'Last Name (Thai)', { type: 'person', key: 'lastName' }, person.lastName, personData.lastName);
          pushItem('Person', 'First Name (English)', { type: 'person', key: 'firstName_eng' }, person.firstName_eng, personData.firstName_eng);
          pushItem('Person', 'Last Name (English)', { type: 'person', key: 'lastName_eng' }, person.lastName_eng, personData.lastName_eng);
          pushItem('Person', 'Title', { type: 'person', key: 'title' }, person.title, personData.title);
          pushItem('Person', 'Email', { type: 'person', key: 'email' }, person.email, personData.email);
          pushItem('Person', 'Phone', { type: 'person', key: 'phone' }, person.phone, personData.phone);
        }

        if (result.enrichedData.company) {
          const company = result.enrichedData.company;
          // Handle Thai company name first (for Thai companies)
          const englishCompanyName = company.name_eng || company.companyName_eng;
          pushItem('Organization', 'Organization Name (Thai)', { type: 'company', key: 'companyName' }, company.name, companyData.companyName);
          pushItem('Organization', 'Organization Name (English)', { type: 'company', key: 'companyName_eng' }, englishCompanyName, companyData.companyName_eng);
          pushItem('Organization', 'Industry', { type: 'company', key: 'industry' }, company.industry, companyData.industry);
          pushItem('Organization', 'Website', { type: 'company', key: 'website' }, company.website, companyData.website);
          pushItem('Organization', 'Address', { type: 'company', key: 'address' }, company.address, companyData.address);
          pushItem('Organization', 'City', { type: 'company', key: 'city' }, company.city, companyData.city);
          pushItem('Organization', 'Country', { type: 'company', key: 'country' }, company.country, companyData.country);
          pushItem('Organization', 'Tax ID', { type: 'company', key: 'taxID' }, company.taxID, companyData.taxID);
          pushItem('Organization', 'Phone', { type: 'company', key: 'phone' }, company.phone, companyData.phone);
          pushItem('Organization', 'Branch', { type: 'company', key: 'branch' }, company.branch, companyData.branch);
          pushItem('Organization', 'Company Email', { type: 'company', key: 'companyEmail' }, company.companyEmail, companyData.companyEmail);
        }

        if (items.length > 0) {
          setEnrichmentReport({ isOpen: true, items, searchTerms: result.searchTerms || [], sources: result.sources || 'unknown' });
        } else {
          // Debug: Check why no fields were filled
          console.warn('⚠️ No fields filled. Enriched data:', result.enrichedData);
          if (result.enrichedData && (result.enrichedData.person || result.enrichedData.company)) {
            console.warn('⚠️ Enriched data exists but no fields matched:', {
              personKeys: Object.keys(result.enrichedData.person || {}),
              companyKeys: Object.keys(result.enrichedData.company || {}),
              currentPersonData: personData,
              currentCompanyData: companyData
            });
          }
          toast('No additional data found to enrich', { icon: 'ℹ️' });
        }
      } else {
        toast('No additional data found to enrich', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Enrichment error:', error);
      toast.error('Failed to enrich customer data: ' + error.message);
    } finally {
      setEnriching(false);
    }
  };

  // Auto-enrich when navigated from detail with ?autoEnrich=1
  useEffect(() => {
    if (mode !== 'edit' || !customer?.CustomerID) return;
    if (autoEnrichTriggeredRef.current) return;
    const params = new URLSearchParams(location.search || '');
    if (params.get('autoEnrich') === '1') {
      autoEnrichTriggeredRef.current = true;
      handleEnrichData();
    }
  }, [mode, customer?.CustomerID, location.search]);

  // Lead Scout: Search for potential leads from customer updates
  const handleLeadScout = async () => {
    if (mode !== 'edit' || !customer?.CustomerID) {
      toast.error('Lead Scout is only available for existing customers');
      return;
    }

    setScouting(true);
    try {
      // Prefer Thai content when customer's name appears to be Thai
      const thaiRegex = /[\u0E00-\u0E7F]/;
      const thaiNamePresent = (
        (customer.FirstName && thaiRegex.test(customer.FirstName)) ||
        (customer.LastName && thaiRegex.test(customer.LastName)) ||
        (customer.CustomerName && thaiRegex.test(customer.CustomerName)) ||
        (customer.CompanyName && thaiRegex.test(customer.CompanyName)) ||
        (customer.PersonCompanyName && thaiRegex.test(customer.PersonCompanyName))
      );

      const result = await customerLeadScout(
        customer.CustomerID,
        thaiNamePresent ? { languagePreference: 'th' } : {}
      );
      
      // Update Customer Summary field with scout results
      if (result.customerSummary) {
        setCustomerSummary(result.customerSummary);
        toast.success('Customer Summary updated with scout findings!');
      } else {
        // Add minimal scout record even if no results found
        const scoutDate = new Date().toLocaleDateString();
        const noResultsMessage = `🔍 Lead Scout (${scoutDate}):\nNo recent news or updates found at this time.\n\n`;
        setCustomerSummary(prev => noResultsMessage + (prev || ''));
        toast.success('Scout completed. No new leads found.');
      }

      // Show summary of findings
      if (result.foundLeads && result.potentialLeads.length > 0) {
        toast.success(`Found ${result.potentialLeads.length} potential lead(s)! Check Customer Summary.`, { 
          duration: 5000,
          icon: '🎯'
        });
      }

    } catch (error) {
      console.error('Lead scout error:', error);
      toast.error(error.message || 'Failed to scout for leads');
    } finally {
      setScouting(false);
    }
  };

  // Reset company association
  const resetCompanyAssociation = () => {
    setSelectedCompanyId('');
    setShowNewCompany(false);
    setCompanyData({
      companyName: '',
      companyName_eng: '',
      industry: '',
      website: '',
      address: '',
      city: '',
      country: '',
      phone: '',
      branch: '',
      companyEmail: '',
      taxID: ''
    });
  };

  // Clear company association completely
  // Handle creating a new person for this company
  const handleCreateNewPerson = () => {
    if (!customer || !customer.CustomerID) return;
    
    // Navigate to create customer page with company pre-selected
    const companyInfo = {
      companyId: customer.CustomerID,
      companyName: customer.CompanyName || companyData.companyName,
      companyName_eng: customer.CompanyName_Eng || companyData.companyName_eng
    };
    
    navigate('/customers/new', {
      state: {
        customerType: 'Person',
        preselectedCompany: companyInfo
      }
    });
  };

  // Handle clicking on an associated person
  const handlePersonClick = (person) => {
    navigate(`/customers/${person.CustomerID}`);
  };

  // Show loading state while fetching customer data or config
  if (loading || configLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col justify-center items-center h-64 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 dark:border-teal-400"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {configLoading ? 'Loading configuration...' : 'Loading customer data...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || configError) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
            {configError ? 'Configuration Error' : 'Error'}
          </h3>
          <p className="text-red-600 dark:text-red-400">
            {configError || error}
          </p>
          {configError && (
            <p className="text-sm text-red-500 dark:text-red-400 mt-2">
              Unable to load entity configuration. Please try refreshing the page or contact support.
            </p>
          )}
          <button
            onClick={() => navigate('/customers')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={onCancel || (() => navigate('/customers'))}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {mode === 'create' ? safeT('customers.createCustomer', 'Create Customer') : safeT('customers.editCustomer', 'Edit Customer')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {mode === 'create' ? safeT('customers.createSubtitle', 'Add a new customer to your database') : safeT('customers.editSubtitle', 'Update customer information')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowHelpModal(true)}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="ดูคู่มือการใช้งาน"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
          {mode === 'edit' && customer?.CustomerID && (
            <button
              type="button"
              onClick={handleLeadScout}
              disabled={scouting || enriching || formLoading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Scout for potential leads from customer updates on social media and web"
            >
              {scouting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Target className="h-4 w-4" />
              )}
              <span>{scouting ? 'Scouting...' : 'Lead Scout'}</span>
            </button>
          )}
          {/* Enrichment button available in both create and edit modes */}
          <button
            type="button"
            onClick={handleEnrichData}
            disabled={enriching || formLoading || scouting || !canEnableEnrichment()}
            className="bg-cyan-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canEnableEnrichment() 
                ? customerType === 'Person' 
                  ? 'Please fill in First Name and either Last Name or Company Name to enable enrichment'
                  : 'Please fill in Company Name to enable enrichment'
                : mode === 'create'
                  ? 'Enrich customer data using AI and search (customer will be created first)'
                  : 'Enrich customer data using AI and search'
            }
          >
            {enriching ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            <span>{enriching ? 'Enriching...' : 'Enrich Data'}</span>
          </button>
          <button
            onClick={handleSubmit}
            disabled={formLoading || scouting || enriching}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {formLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{formLoading ? safeT('common.saving', 'Saving...') : safeT('common.save', 'Save')}</span>
          </button>
        </div>
      </div>

      {/* Form */}
       <form key={`customer-form-${customer?.CustomerID || 'new'}`} onSubmit={handleSubmit} className="space-y-6">
        {/* Validation Summary */}
        <ValidationSummary 
          errors={errors} 
          onFieldClick={(field) => {
            const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus?.();
            }
          }}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
        {/* Customer Type Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {safeT('customers.customerType', 'Customer Type')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              {customerTypeOptions.map(option => {
                const IconComponent = option.value === 'Person' ? User : Building;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setCustomerType(option.value);
                      // Reset company association when switching type
                      if (customerType !== option.value) {
                        resetCompanyAssociation();
                      }
                    }}
                    className={`p-4 rounded-lg border-2 flex items-center space-x-3 transition-colors ${
                      customerType === option.value
                        ? 'border-teal-500 dark:border-blue-400 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
                    }`}
                  >
                    <IconComponent className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                    <div className="text-left">
                      <div className="font-medium text-gray-900 dark:text-white">{option.label}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {option.value === 'Person' ? safeT('customers.individualDesc', 'Individual person') : safeT('customers.companyDesc', 'Business or organization')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Customer Code */}
          {!isFieldHidden('CustomerCode') && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {safeT('customers.customerCode', 'Customer Code')}
              </label>
              <input
                type="text"
                value={customerCode}
                onChange={(e) => setCustomerCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="e.g., CUST-2025-0001"
                readOnly={mode === 'edit'}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {mode === 'edit' ? safeT('customers.customerCodeReadOnly', 'Customer code cannot be changed after creation') : safeT('customers.customerCodeHelp', 'Auto-generated if not provided')}
              </p>
            </div>
          )}
        </div>

        {/* Person Form */}
        {customerType === 'Person' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('customers.personalInfo', 'Personal Information')}</h3>
            </div>

            <div className="space-y-6">
              {/* Profile Summary Header */}
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start space-x-3 min-w-0 flex-1">
                    {/* Avatar with person icon */}
                    <div className="h-12 w-12 flex-shrink-0 rounded-full bg-teal-600 dark:bg-teal-500 text-white flex items-center justify-center shadow">
                      <User className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">
                        {`${personData.firstName || ''} ${personData.lastName || ''}`.trim() || safeT('customers.newPerson', 'New Person')}
                      </div>
                      {(personData.firstName_eng || personData.lastName_eng) && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 break-words">
                          {`${personData.firstName_eng || ''} ${personData.lastName_eng || ''}`.trim()}
                        </div>
                      )}
                      {personData.title && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">{personData.title}</div>
                      )}
                    </div>
                  </div>
                  {/* Organization Chip (if company is selected) */}
                  {selectedCompanyId && companiesList.find(c => String(c.CompanyID) === String(selectedCompanyId)) && (() => {
                    const selectedCompany = companiesList.find(c => String(c.CompanyID) === String(selectedCompanyId));
                    const displayName = selectedCompany?.Branch && selectedCompany.Branch.trim() 
                      ? `${selectedCompany.CompanyName} (${selectedCompany.Branch})`
                      : selectedCompany?.CompanyName;
                    return (
                      <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 w-full sm:w-auto justify-center sm:justify-start flex-shrink-0">
                        <Building className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium truncate max-w-[200px] sm:max-w-[180px] md:max-w-[220px]">
                          {displayName}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>


              {/* Personal Information Fields - Title, Nickname, First Name, Last Name, English Names, Email, Phone, Job Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isFieldHidden('PersonTitle') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.title', 'Title')}
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={personData.title}
                        onChange={(e) => handlePersonChange('title', e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder={safeT('customers.titlePlaceholder', 'เช่น นาย, นาง, นางสาว')}
                      />
                    </div>
                  </div>
                )}
                {!isFieldHidden('NickName') && customerType === 'Person' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.nickName', 'Nick Name')}
                    </label>
                    <input
                      type="text"
                      value={personData.nickName || ''}
                      onChange={(e) => handlePersonChange('nickName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder={safeT('customers.nickNamePlaceholder', 'ส้ม')}
                    />
                  </div>
                )}
                {!isFieldHidden('FirstName') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.firstName', 'First Name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={personData.firstName}
                      onChange={(e) => handlePersonChange('firstName', e.target.value)}
                      className={getInputClassName(!!errors.firstName)}
                      placeholder="เช่น สมชาย"
                    />
                    <FieldError error={errors.firstName} />
                  </div>
                )}
                {!isFieldHidden('LastName') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.lastName', 'Last Name')}
                    </label>
                    <input
                      type="text"
                      value={personData.lastName}
                      onChange={(e) => handlePersonChange('lastName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                        errors.lastName ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="เช่น ใจดี"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName}</p>
                    )}
                  </div>
                )}
                {!isFieldHidden('FirstName_Eng') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.firstName', 'First Name')} ({safeT('common.english', 'English')})
                    </label>
                    <input
                      type="text"
                      value={personData.firstName_eng}
                      onChange={(e) => handlePersonChange('firstName_eng', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="e.g. Somchai"
                    />
                  </div>
                )}
                {!isFieldHidden('LastName_Eng') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.lastName', 'Last Name')} ({safeT('common.english', 'English')})
                    </label>
                    <input
                      type="text"
                      value={personData.lastName_eng}
                      onChange={(e) => handlePersonChange('lastName_eng', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="e.g. Jaidee"
                    />
                  </div>
                )}
                {!isFieldHidden('PersonEmail') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.email', 'Email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={personData.email}
                        onChange={(e) => handlePersonChange('email', e.target.value)}
                        onBlur={(e) => handlePersonChange('email', normalizeEmailList(e.target.value))}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors dark:bg-gray-700 dark:text-white ${
                          errors.email ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-gray-300 dark:border-gray-600 focus:border-teal-500 focus:ring-teal-200'
                        }`}
                        placeholder={safeT('customers.emailPlaceholder', 'e.g., john@example.com')}
                      />
                    </div>
                    <FieldError error={errors.email} />
                  </div>
                )}
                {!isFieldHidden('PersonPhone') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.phone', 'Phone')}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <input
                        type="tel"
                        value={personData.phone}
                        onChange={(e) => handlePersonChange('phone', e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder={safeT('customers.phonePlaceholder', 'e.g., +66 123 456 789')}
                      />
                    </div>
                  </div>
                )}
                {!isFieldHidden('JobTitle') && customerType === 'Person' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.jobTitle', 'Job Title')}
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 z-10" />
                      {getSuggestionConfig(entityConfig, 'JobTitle') ? (
                        <AutocompleteTextField
                          entityName="customer"
                          fieldName="JobTitle"
                          value={personData.jobTitle || ''}
                          onChange={(e) => handlePersonChange('jobTitle', e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder={safeT('customers.jobTitlePlaceholder', 'e.g., Sales Manager')}
                          suggestConfig={getSuggestionConfig(entityConfig, 'JobTitle')}
                        />
                      ) : (
                        <input
                          type="text"
                          value={personData.jobTitle || ''}
                          onChange={(e) => handlePersonChange('jobTitle', e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder={safeT('customers.jobTitlePlaceholder', 'e.g., Sales Manager')}
                        />
                      )}
                    </div>
                  </div>
                )}
                {!isFieldHidden('Department') && customerType === 'Person' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.department', 'Department')}
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={personData.department || ''}
                        onChange={(e) => handlePersonChange('department', e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder={safeT('customers.departmentPlaceholder', 'e.g., Sales, Marketing, IT')}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Organization */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-green-900 dark:text-green-100 flex items-center">
                    <Building className="h-4 w-4 mr-2" />
                    🏢 {safeT('customers.company', 'Organization')}
                  </h4>
                </div>
                
                {/* Associate with Company */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {safeT('customers.associateCompany', 'Associate with Company')}
                  </label>
                  <div className="flex items-center space-x-4">
                    <select
                      value={selectedCompanyId}
                      onChange={(e) => { 
                        const companyId = e.target.value;
                        setSelectedCompanyId(companyId); 
                        setShowNewCompany(false);
                        
                        // Clear company form data when selecting existing company
                        if (companyId) {
                          setCompanyData({
                            companyName: '',
                            companyName_eng: '',
                            industry: '',
                            website: '',
                            address: '',
                            city: '',
                            country: '',
                            phone: '',
                            branch: '',
                            companyEmail: '',
                            taxID: ''
                          });
                        } else {
                          // When clearing selection, also clear company data
                          setCompanyData({
                            companyName: '',
                            companyName_eng: '',
                            industry: '',
                            website: '',
                            address: '',
                            city: '',
                            country: '',
                            phone: '',
                            branch: '',
                            companyEmail: '',
                            taxID: ''
                          });
                        }
                      }}
                      disabled={launchedFromCompanyDetail}
                      className={`flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 ${
                        launchedFromCompanyDetail ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="">{safeT('customers.selectCompany', 'Select Company')}</option>
                      {companiesList.map(c => {
                        const displayName = c.Branch && c.Branch.trim() 
                          ? `${c.CompanyName} (${c.Branch})`
                          : c.CompanyName;
                        return (
                          <option key={c.CompanyID} value={String(c.CompanyID)}>{displayName}</option>
                        );
                      })}
                    </select>
                    {!launchedFromCompanyDetail && (
                    <button
                      type="button"
                      onClick={() => { 
                        setShowNewCompany(true); 
                        setSelectedCompanyId('');
                      }}
                      className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-md hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
                    >
                      {safeT('customers.addNewCompany', 'Add New Company')}
                    </button>
                    )}
                  </div>
                  
                  {/* New Company Form */}
                  {showNewCompany && (
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Thai Company Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {safeT('customers.companyName', 'Company Name')} (Thai) *
                          </label>
                          {getSuggestionConfig(entityConfig, 'CompanyName') ? (
                            <AutocompleteTextField
                              entityName="customer"
                              fieldName="CompanyName"
                              value={companyData.companyName}
                              onChange={(e) => handleCompanyChange('companyName', e.target.value)}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800 focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400"
                              placeholder="ชื่อบริษัท"
                              suggestConfig={getSuggestionConfig(entityConfig, 'CompanyName')}
                            />
                          ) : (
                            <input
                              type="text"
                              value={companyData.companyName}
                              onChange={(e) => handleCompanyChange('companyName', e.target.value)}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800 focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400"
                              placeholder="ชื่อบริษัท"
                            />
                          )}
                          {errors.companyName && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.companyName}</p>}
                        </div>
                        {/* English Company Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {safeT('customers.companyName', 'Company Name')} (English)
                          </label>
                          <input
                            type="text"
                            value={companyData.companyName_eng}
                            onChange={(e) => handleCompanyChange('companyName_eng', e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800 focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400"
                            placeholder="Company Name"
                          />
                        </div>
                      </div>
                      
                      {/* Branch - follows company name */}
                      {!isFieldHidden('Branch') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {safeT('customers.branch', 'Branch')}
                          </label>
                          <input
                            type="text"
                            value={companyData.branch || ''}
                            onChange={(e) => handleCompanyChange('branch', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                            placeholder={safeT('customers.branchPlaceholder', 'e.g., Bangkok Branch')}
                          />
                        </div>
                      )}
                      
                      {/* Other Company Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!isFieldHidden('Industry') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {safeT('customers.industry', 'Industry')}
                        </label>
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                          {industryOptions.length > 0 ? (
                            <select
                              value={companyData.industry}
                              onChange={(e) => handleCompanyChange('industry', e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="">{safeT('customers.selectIndustry', 'Select industry')}</option>
                              {industryOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={companyData.industry}
                              onChange={(e) => handleCompanyChange('industry', e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                              placeholder={safeT('customers.industryPlaceholder', 'e.g., Technology')}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    
                    {!isFieldHidden('Website') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {safeT('customers.website', 'Website')}
                        </label>
                        <div className="relative">
                          <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <input
                            type="text"
                            value={companyData.website}
                            onChange={(e) => handleCompanyChange('website', e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                            placeholder={safeT('customers.websitePlaceholder', 'e.g., www.company.com')}
                          />
                        </div>
                      </div>
                    )}
                    
                    {!isFieldHidden('CompanyPhone') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {safeT('customers.phone', 'Phone')}
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <input
                            type="tel"
                            value={companyData.phone}
                            onChange={(e) => handleCompanyChange('phone', e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                            placeholder={safeT('customers.phonePlaceholder', 'e.g., +66 123 456 789')}
                          />
                        </div>
                      </div>
                    )}
                    
                    {!isFieldHidden('CompanyEmail') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {safeT('customers.companyEmail', 'Company Email')}
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <input
                            type="email"
                            value={companyData.companyEmail || ''}
                            onChange={(e) => handleCompanyChange('companyEmail', e.target.value)}
                            onBlur={(e) => handleCompanyChange('companyEmail', normalizeEmailList(e.target.value))}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                            placeholder={safeT('customers.companyEmailPlaceholder', 'e.g., info@company.com')}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                    
                    {/* Address field - full width */}
                    {!isFieldHidden('CompanyAddress') && (
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {safeT('customers.address', 'Address')}
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <input
                            type="text"
                            value={companyData.address}
                            onChange={(e) => handleCompanyChange('address', e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                            placeholder={safeT('customers.addressPlaceholder', 'e.g., 123 Main Street')}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* City, Country, and Tax ID - next row in grid */}
                    {(!isFieldHidden('City') || !isFieldHidden('Country') || !isFieldHidden('TaxID')) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        {!isFieldHidden('City') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {safeT('customers.city', 'City')}
                            </label>
                            <input
                              type="text"
                              value={companyData.city}
                              onChange={(e) => handleCompanyChange('city', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                              placeholder={safeT('customers.cityPlaceholder', 'e.g., Bangkok')}
                            />
                          </div>
                        )}
                        
                        {!isFieldHidden('Country') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {safeT('customers.country', 'Country')}
                            </label>
                            <input
                              type="text"
                              value={companyData.country}
                              onChange={(e) => handleCompanyChange('country', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                              placeholder={safeT('customers.countryPlaceholder', 'e.g., Thailand')}
                            />
                          </div>
                        )}
                        
                        {!isFieldHidden('TaxID') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {safeT('customers.taxID', 'Tax ID')}
                            </label>
                            <input
                              type="text"
                              value={companyData.taxID || ''}
                              onChange={(e) => handleCompanyChange('taxID', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-100 dark:placeholder-gray-800"
                              placeholder={safeT('customers.taxIDPlaceholder', 'e.g., 1234567890123')}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Company Form */}
        {customerType === 'Company' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('customers.companyInfo', 'Company Information')}</h3>
            </div>

            <div className="space-y-6">
              {/* Company Profile Summary Header */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start space-x-3 min-w-0 flex-1">
                    {/* Company Icon */}
                    <div className="h-12 w-12 flex-shrink-0 rounded-full bg-green-600 dark:bg-green-500 text-white flex items-center justify-center text-lg font-semibold shadow">
                      <Building className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">
                        {companyData.companyName || safeT('customers.newCompany', 'New Company')}
                      </div>
                      {companyData.companyName_eng && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 break-words">
                          {companyData.companyName_eng}
                        </div>
                      )}
                      {companyData.industry && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">{companyData.industry}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>


              {/* Organization Details */}
              {hasVisibleFieldsInGroup(['CompanyName', 'CompanyName_Eng', 'Branch', 'Industry', 'CompanyAddress', 'City', 'Country', 'CompanyPhone', 'Website', 'CompanyEmail']) && (
                <div className="space-y-4">
                  {/* Organization Name (Thai) */}
                  {!isFieldHidden('CompanyName') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.companyNameThai', 'Organization Name (Thai)')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={companyData.companyName}
                        onChange={(e) => handleCompanyChange('companyName', e.target.value)}
                        className={getInputClassName(!!errors.companyName)}
                        placeholder="เช่น บริษัท เทคโนโลยี จำกัด"
                      />
                      <FieldError error={errors.companyName} />
                    </div>
                  )}
                  {!isFieldHidden('CompanyName_Eng') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.companyNameEng', 'Organization Name (English)')}
                      </label>
                      <input
                        type="text"
                        value={companyData.companyName_eng}
                        onChange={(e) => handleCompanyChange('companyName_eng', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="e.g. Technology Company Limited"
                      />
                    </div>
                  )}
                  {!isFieldHidden('Branch') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.branch', 'Branch')}
                      </label>
                      <input
                        type="text"
                        value={companyData.branch || ''}
                        onChange={(e) => handleCompanyChange('branch', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder={safeT('customers.branchPlaceholder', 'e.g., Bangkok Branch')}
                      />
                    </div>
                  )}

                  {!isFieldHidden('Industry') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.industry', 'Industry')}
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                        {industryOptions.length > 0 ? (
                          <select
                            value={companyData.industry}
                            onChange={(e) => handleCompanyChange('industry', e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">{safeT('customers.selectIndustry', 'Select industry')}</option>
                            {industryOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={companyData.industry}
                            onChange={(e) => handleCompanyChange('industry', e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder={safeT('customers.industryPlaceholder', 'e.g., Technology')}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {!isFieldHidden('CompanyAddress') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.address', 'Address')}
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={companyData.address}
                          onChange={(e) => handleCompanyChange('address', e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder={safeT('customers.addressPlaceholder', 'e.g., 123 Main Street')}
                        />
                      </div>
                    </div>
                  )}
                    {hasVisibleFieldsInGroup(['City', 'Country', 'TaxID']) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {!isFieldHidden('City') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.city', 'City')}
                            </label>
                            <input
                              type="text"
                              value={companyData.city}
                              onChange={(e) => handleCompanyChange('city', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder={safeT('customers.cityPlaceholder', 'e.g., Bangkok')}
                            />
                          </div>
                        )}
                        {!isFieldHidden('Country') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.country', 'Country')}
                            </label>
                            <input
                              type="text"
                              value={companyData.country}
                              onChange={(e) => handleCompanyChange('country', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder={safeT('customers.countryPlaceholder', 'e.g., Thailand')}
                            />
                          </div>
                        )}
                        {!isFieldHidden('TaxID') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.taxID', 'Tax ID')}
                            </label>
                            <input
                              type="text"
                              value={companyData.taxID || ''}
                              onChange={(e) => handleCompanyChange('taxID', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder={safeT('customers.taxIDPlaceholder', 'e.g., 1234567890123')}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  {hasVisibleFieldsInGroup(['CompanyPhone', 'Website', 'CompanyEmail']) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {!isFieldHidden('CompanyPhone') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.phone', 'Phone')}
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                            <input
                              type="tel"
                              value={companyData.phone}
                              onChange={(e) => handleCompanyChange('phone', e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder={safeT('customers.phonePlaceholder', 'e.g., +66 123 456 789')}
                            />
                          </div>
                        </div>
                      )}
                      {!isFieldHidden('Website') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.website', 'Website')}
                          </label>
                          <div className="relative">
                            <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                            <input
                              type="text"
                              value={companyData.website}
                              onChange={(e) => handleCompanyChange('website', e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder={safeT('customers.websitePlaceholder', 'e.g., www.company.com')}
                            />
                          </div>
                        </div>
                      )}
                      {!isFieldHidden('CompanyEmail') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.companyEmail', 'Company Email')}
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                            <input
                              type="email"
                              value={companyData.companyEmail || ''}
                              onChange={(e) => handleCompanyChange('companyEmail', e.target.value)}
                              onBlur={(e) => handleCompanyChange('companyEmail', normalizeEmailList(e.target.value))}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder={safeT('customers.companyEmailPlaceholder', 'e.g., info@company.com')}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        )}

        {/* Customer Summary - Separate section for both Person and Company */}
        {!isFieldHidden('CustomerSummary') && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {safeT('customers.customerSummary', 'Customer Summary')}
              </h3>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {safeT('customers.customerSummary', 'Customer Summary')}
              </label>
              <textarea
                value={customerSummary}
                onChange={(e) => setCustomerSummary(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                rows="4"
                placeholder={safeT('customers.customerSummaryPlaceholder', 'Enter a summary or notes about this customer...')}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {safeT('customers.customerSummaryHelp', 'Add a brief summary or notes about this customer')}
              </p>
            </div>
          </div>
        )}

        {/* Dynamic Fields from Entity Config (fields not already hardcoded) */}
        {(() => {
          const dynamicFields = getDynamicFormFields();
          if (dynamicFields.length === 0) return null;
          
          // Group fields by their group property
          const groupedFields = {};
          dynamicFields.forEach(field => {
            const group = field.group || field.ui?.group || 'Additional Information';
            if (!groupedFields[group]) groupedFields[group] = [];
            groupedFields[group].push(field);
          });
          
          return Object.entries(groupedFields).map(([groupName, fields]) => (
            <div key={groupName} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {groupName}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.map(field => renderDynamicFormField(field))}
              </div>
            </div>
          ));
        })()}

        {/* Customer Additional Data */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <CustomAttributesEditor
            fields={customAttributeFields}
            attributes={attributes}
            onChange={(next) => setAttributes(next)}
            onAddField={handleAddCustomAttributeField}
            title={safeT('customers.additionalDataSection', 'Additional Data')}
            allExistingFieldNames={entityConfig?.config?.fields ? Object.keys(entityConfig.config.fields) : []}
            entityName="customer"
            onFieldsUpdated={async () => {
              // Clear config cache and trigger reload
              clearConfigCache('customer');
              window.dispatchEvent(new CustomEvent('entity-config-updated', { 
                detail: { entityName: 'customer' } 
              }));
            }}
          />
        </div>

        {/* Associated Persons - Only show for Company customers in edit mode */}
        {mode === 'edit' && customerType === 'Company' && customer && customer.CustomerID && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {safeT('customers.associatedPersons', 'Associated Persons')}
                </h3>
                {associatedPersons.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-sm font-medium rounded-full">
                    {associatedPersons.length}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleCreateNewPerson}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>{safeT('customers.createNewPerson', 'Create New Person')}</span>
              </button>
            </div>
            
            {loadingAssociatedPersons ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 dark:border-teal-400"></div>
              </div>
            ) : associatedPersons.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {associatedPersons.map((person) => (
                  <div 
                    key={person.CustomerID} 
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    onClick={() => handlePersonClick(person)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        {/* Avatar with person icon */}
                        <div className="h-10 w-10 rounded-full bg-teal-600 dark:bg-teal-500 text-white flex items-center justify-center shadow flex-shrink-0">
                          <User className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Thai Name */}
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 hover:text-teal-600 dark:hover:text-teal-400 transition-colors truncate">
                            {person.FirstName} {person.LastName}
                          </h4>
                          
                          {/* English Name */}
                          {(person.FirstName_Eng || person.LastName_Eng) && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {person.FirstName_Eng} {person.LastName_Eng}
                            </p>
                          )}
                          
                          {/* Contact Info */}
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {person.PersonEmail && (
                              <div className="flex items-center space-x-1">
                                <Mail className="h-3 w-3" />
                                <span className="truncate max-w-[200px]">{person.PersonEmail}</span>
                              </div>
                            )}
                            {person.PersonPhone && (
                              <div className="flex items-center space-x-1">
                                <Phone className="h-3 w-3" />
                                <span>{person.PersonPhone}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Job Title */}
                          {person.PersonTitle && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center space-x-1">
                              <Briefcase className="h-3 w-3" />
                              <span>{person.PersonTitle}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Arrow icon */}
                      <div className="ml-4">
                        <ArrowLeft className="h-4 w-4 text-gray-400 dark:text-gray-500 transform rotate-180" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {safeT('customers.noAssociatedPersons', 'No associated persons found for this company')}
                </p>
                <button
                  type="button"
                  onClick={handleCreateNewPerson}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>{safeT('customers.createFirstPerson', 'Create First Person')}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Attachments (Edit Mode Only) */}
        {mode === 'edit' && customer && customer.CustomerID && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('customers.attachments', 'Attachments')}</h3>
            </div>
            
            <AttachmentServiceV2 
              entityType="Customer" 
              entityId={customer.CustomerID} 
              onAttachmentChange={() => {
                // Optional: Add any callback logic here
              }}
            />
          </div>
        )}

          </div>
          <div className="space-y-6">
            {/* Related Entities - moved to right column */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('activities.relatedEntities', 'Relationships')}</h3>
                </div>
                {relatedEntities.length > 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {relatedEntities.length} {relatedEntities.length === 1 ? 'entity' : 'entities'} selected
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { value: 'Lead', label: safeT('leads.subtitle', 'Leads') },
                  { value: 'Opportunity', label: safeT('opportunities.subtitle', 'Opportunities') },
                  { value: 'Activity', label: safeT('activities.title', 'Activities') },
                  { value: 'SaleNote', label: safeT('salesNotes.pagetitle', 'Notes') }
                ].map(type => {
                  const count = relatedEntities.filter(e => e.type === type.value).length;
                  const icon = () => {
                    switch (type.value) {
                      case 'Lead': return <Target className="h-5 w-5" />;
                      case 'Opportunity': return <Target className="h-5 w-5" />;
                      case 'Activity': return <Calendar className="h-5 w-5" />;
                      case 'SaleNote': return <FileText className="h-5 w-5" />;
                      default: return <FileText className="h-5 w-5" />;
                    }
                  };
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => openEntitySelectionModal(type.value, '')}
                      className={`group relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
                        count > 0
                          ? 'border-teal-500 dark:border-blue-400 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-400 dark:hover:border-teal-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <div className={`mb-2 ${count > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-teal-500'}`}>{icon()}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white text-center">{type.label}</div>
                      {count > 0 && (
                        <div className="absolute -top-2 -right-2 bg-teal-500 dark:bg-teal-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">{count}</div>
                      )}
                      <Plus className={`absolute bottom-1 right-1 h-4 w-4 ${count > 0 ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'} text-gray-400 dark:text-gray-500`} />
                    </button>
                  );
                })}
              </div>
              {relatedEntities.length > 0 && (
                <div className="space-y-3">
                  {['Lead','Opportunity','Activity','SaleNote'].map(typeValue => {
                    const items = relatedEntities.filter(e => e.type === typeValue);
                    if (items.length === 0) return null;
                    const iconSmall = () => {
                      switch (typeValue) {
                        case 'Lead': return <Target className="h-4 w-4" />;
                        case 'Opportunity': return <Target className="h-4 w-4" />;
                        case 'Activity': return <Calendar className="h-4 w-4" />;
                        case 'SaleNote': return <FileText className="h-4 w-4" />;
                        default: return <FileText className="h-4 w-4" />;
                      }
                    };
                    return (
                      <div key={typeValue} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          {iconSmall()}
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{typeValue} ({items.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {items.map((entity, idx) => (
                            <div key={`${entity.type}-${entity.id}-${idx}`} className="group flex items-center space-x-2 px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors">
                              {iconSmall()}
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{entity.name || `${typeValue} ${entity.id}`}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">#{entity.id}</span>
                              <button type="button" onClick={() => setRelatedEntities(prev => prev.filter(e => !(e.type === entity.type && e.id === entity.id)))} className="ml-1 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" title={`Remove ${entity.name}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={() => openEntitySelectionModal(typeValue, '')} className="flex items-center space-x-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 text-sm font-medium transition-colors">
                            <Plus className="h-3 w-3" />
                            <span>Add {typeValue}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Salesperson Assignment - moved below related entities */}
            {!isFieldHidden('SalespersonID') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('customers.salespersonInfo', 'Salesperson Information')}</h3>
                </div>

                {companySalespersonSuggestion && (
                  <div className="mb-4 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-teal-800 dark:text-teal-200 font-medium">
                          💡 {(t('customers.salespersonSuggestion', {
                            count: companySalespersonSuggestion.personCount,
                            name: companySalespersonSuggestion.salespersonName
                          }) !== 'customers.salespersonSuggestion') ? t('customers.salespersonSuggestion', {
                            count: companySalespersonSuggestion.personCount,
                            name: companySalespersonSuggestion.salespersonName
                          }) : `${companySalespersonSuggestion.personCount} associated person(s) have ${companySalespersonSuggestion.salespersonName} assigned. Would you like to assign this salesperson to the company?`}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                          {safeT('customers.salespersonSuggestionAction', 'Click the button below to assign this salesperson to the company')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedSalespersonId(companySalespersonSuggestion.salespersonId);
                          setCompanySalespersonSuggestion(null);
                          toast.success(safeT('customers.salespersonAssigned', 'Salesperson assigned successfully'));
                        }}
                        className="ml-3 px-3 py-1.5 bg-teal-600 dark:bg-teal-500 text-white text-sm rounded-md hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors flex items-center space-x-1"
                      >
                        <Users className="h-3 w-3" />
                        <span>{safeT('customers.salespersonAssignButton', 'Assign Salesperson')}</span>
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('customers.assignedSalesperson', 'Assigned Salesperson')}
                  </label>
                  <select
                    value={selectedSalespersonId}
                    onChange={(e) => {
                      const newSalespersonId = e.target.value;
                      
                      // Set original salesperson ID if not set yet (on initial load)
                      if (!originalSalespersonID && newSalespersonId && mode === 'edit') {
                        setOriginalSalespersonID(newSalespersonId);
                      }
                      
                      // Check for salesperson change warning
                      if (mode === 'edit' && originalSalespersonID) {
                        const hasChanged = newSalespersonId !== originalSalespersonID && newSalespersonId !== '';
                        const currentUserId = String(user?.tenantUserId || user?.userId || user?.UserID || '');
                        
                        // Show warning if policy is 1 or 2 and user is changing salesperson to someone else
                        if (hasChanged && dataGovernancePolicy) {
                          const policyType = Number(dataGovernancePolicy.policyType || dataGovernancePolicy.PolicyType || 0);
                          const isPolicy1Or2 = policyType === 1 || policyType === 2;
                          const isChangingToOther = newSalespersonId !== currentUserId;
                          
                          console.log('🔍 CustomerForm: Salesperson change check:', {
                            originalSalespersonID,
                            newSalespersonId,
                            currentUserId,
                            policyType,
                            isPolicy1Or2,
                            isChangingToOther,
                            dataGovernancePolicy
                          });
                          
                          if (isPolicy1Or2 && isChangingToOther) {
                            setShowSalespersonWarning(true);
                          } else {
                            setShowSalespersonWarning(false);
                          }
                        } else {
                          setShowSalespersonWarning(false);
                        }
                      } else {
                        setShowSalespersonWarning(false);
                      }
                      
                      setSelectedSalespersonId(newSalespersonId);
                      if (errors.salesperson) {
                        setErrors(prev => ({
                          ...prev,
                          salesperson: null
                        }));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors.salesperson ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    <option value="">{safeT('customers.selectSalesperson', 'Select Salesperson')}</option>
                    {salespersonsList.map(salesperson => (
                      <option key={salesperson.UserID} value={salesperson.UserID}>
                        {salesperson.FirstName} {salesperson.LastName}
                      </option>
                    ))}
                  </select>
                  {errors.salesperson && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.salesperson}</p>
                  )}
                  {showSalespersonWarning && (
                    <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start space-x-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                          {safeT('customers.salespersonChangeWarning', 'Warning: Changing the salesperson')}
                        </p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                          {safeT('customers.salespersonChangeWarningDetail', 'Under the current data governance policy, you may no longer be able to view or update this record after saving.')}
                        </p>
                      </div>
                    </div>
                  )}
                  {selectedSalespersonId && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSalespersonId('');
                        setShowSalespersonWarning(false);
                        setErrors(prev => ({
                          ...prev,
                          salesperson: null
                        }));
                      }}
                      className="mt-1 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 underline"
                    >
                      Clear salesperson assignment
                    </button>
                  )}
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {safeT('customers.salespersonHelp', 'Assign a salesperson to manage this customer')}
                  </p>
                </div>
              </div>
            )}
            {/* Tags Section */}
            {mode === 'edit' && customer?.CustomerID && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {safeT('common.tags', 'Tags')}
                  </h3>
                </div>
                <TagService
                  entityType="Customer"
                  entityId={customer.CustomerID}
                  initialTags={customer.Tags ? (typeof customer.Tags === 'string' ? customer.Tags.split(',').map(t => t.trim()).filter(Boolean) : customer.Tags) : []}
                  maxTags={15}
                  onChange={async (newTags) => {
                    // Tags updated successfully
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Spacer to accommodate floating action buttons */}
        <div className="h-24 md:h-32"></div>

        {/* Floating Form Actions */}
        <div className="fixed bottom-6 right-6 lg:right-[max(1.5rem,calc((100vw-72rem)/2))] z-40 flex flex-col-reverse gap-3 md:flex-row">
          <button
            type="button"
            onClick={onCancel || (() => navigate('/customers'))}
            className="inline-flex items-center space-x-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/80 px-5 py-2 shadow-lg shadow-gray-500/10 dark:shadow-black/30 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 transition-colors backdrop-blur"
          >
            <X className="h-4 w-4" />
            <span>{safeT('common.cancel', 'Cancel')}</span>
          </button>
          <button
            type="submit"
            disabled={formLoading}
            className="inline-flex items-center space-x-2 rounded-full bg-teal-600 dark:bg-teal-500 px-6 py-2 shadow-lg shadow-teal-500/30 dark:shadow-teal-900/30 text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-60 transition-colors"
          >
            {formLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{formLoading ? safeT('common.saving', 'Saving...') : safeT('common.save', 'Save')}</span>
          </button>
        </div>
      </form>
      <EntitySelectionModal
        isOpen={entitySelectionModal.isOpen}
        entityType={entitySelectionModal.entityType}
        title={`Select ${entitySelectionModal.entityType || 'Entity'}`}
        subtitle={entitySelectionModal.selectedIds.length > 0 ? `${entitySelectionModal.selectedIds.length} ${safeT('activities.selected', 'selected')}` : undefined}
        searchTerm={entitySelectionModal.searchTerm}
        onSearchTermChange={handleEntityModalSearchChange}
        searchPlaceholder={(t('activities.searchEntity', { entityType: entitySelectionModal.entityType }) !== 'activities.searchEntity') ? t('activities.searchEntity', { entityType: entitySelectionModal.entityType }) : `Search ${entitySelectionModal.entityType || 'entity'}...`}
        loading={entitySelectionModal.loading}
        results={entitySelectionModal.results}
        selectedIds={entitySelectionModal.selectedIds}
        onToggleEntity={toggleEntitySelectionInModal}
        onClose={() => closeEntitySelectionModal(false)}
        onConfirm={() => closeEntitySelectionModal(true)}
        confirmLabel={
          entitySelectionModal.selectedIds.length > 0
            ? `${safeT('common.add', 'Add')} (${entitySelectionModal.selectedIds.length})`
            : safeT('common.add', 'Add')
        }
        confirmIcon={Plus}
        confirmDisabled={entitySelectionModal.selectedIds.length === 0}
        cancelLabel={safeT('common.cancel', 'Cancel')}
        emptyMessage={safeT('activities.noResultsFound', 'No results found')}
        idleMessage={safeT('activities.startTypingToSearch', 'Start typing to search')}
        loadingMessage={safeT('activities.searching', 'Searching...')}
        resolveId={(entity, type) => getEntityId(entity, type || entitySelectionModal.entityType)}
        resolveName={(entity, type) => getEntityName(entity, type || entitySelectionModal.entityType)}
        resolveSubInfo={(entity, type) => getEntitySubInfo(entity, type || entitySelectionModal.entityType)}
      />

      {/* Save Company as Customer Confirmation Dialog */}
      {saveCompanyDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Building className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {safeT('customers.saveCompanyAsCustomer', 'Save Company as Customer?')}
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  {safeT('customers.saveCompanyAsCustomerMessage', 'You are creating a person customer with a new company. Would you like to also save this company as a separate customer record?')}
                </p>
                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg p-3">
                  <p className="text-sm font-medium text-teal-900 dark:text-teal-100 mb-1">
                    {safeT('customers.companyName', 'Company Name')}:
                  </p>
                  <p className="text-sm text-teal-700 dark:text-teal-300">
                    {saveCompanyDialog.pendingFormData?.newCompany?.name || ''}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  {safeT('customers.saveCompanyAsCustomerNote', 'If you choose "Yes", both the person and company will be created as separate customer records, and the person will be associated with the company.')}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleSaveCompanyCancel}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                >
                  {safeT('common.no', 'No')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveCompanyConfirm}
                  className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
                >
                  {safeT('common.yes', 'Yes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enrichment Report Modal */}
      {enrichmentReport.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Data Enrichment Complete</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {(enrichmentReport.items || []).length} field(s) proposed
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setEnrichmentReport({ isOpen: false, items: [], searchTerms: [], sources: '' })} 
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Selectable Fields */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Select values to apply:</h4>
                  <div className="space-y-2">
                    {(enrichmentReport.items || []).map((item, index) => (
                      <label key={item.id || index} className={`p-3 rounded-lg border flex items-start space-x-3 ${item.differs ? 'border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20' : 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20'}`}>
                        <input type="checkbox" checked={!!item.selected} onChange={(e) => {
                          const checked = e.target.checked;
                          setEnrichmentReport(prev => ({ ...prev, items: prev.items.map((it, i) => i === index ? { ...it, selected: checked } : it) }));
                        }} className="mt-1 h-4 w-4" />
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{item.category}</div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{item.label}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {item.differs && item.originalValue ? (
                              <span>
                                <span className="line-through opacity-70 mr-2">{item.originalValue}</span>
                                <span className="mx-1">→</span>
                                <span className="font-medium text-green-700 dark:text-green-300">{item.value}</span>
                              </span>
                            ) : (
                              <span className="font-medium text-green-700 dark:text-green-300">{item.value}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Search Information */}
                {enrichmentReport.searchTerms.length > 0 && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Search Terms:</h4>
                    <div className="flex flex-wrap gap-2">
                      {enrichmentReport.searchTerms.map((term, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded text-xs"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Sources */}
                {enrichmentReport.sources && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Data Sources:</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {enrichmentReport.sources.replace(/_/g, ' ')}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="space-x-2">
                <button type="button" onClick={() => setEnrichmentReport(prev => ({ ...prev, items: prev.items.map(it => ({ ...it, selected: true })) }))} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">Select All</button>
                <button type="button" onClick={() => setEnrichmentReport(prev => ({ ...prev, items: prev.items.map(it => ({ ...it, selected: false })) }))} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">Deselect All</button>
              </div>
              <div className="space-x-2">
                <button type="button" onClick={() => setEnrichmentReport({ isOpen: false, items: [], searchTerms: [], sources: '' })} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">Close</button>
                <button type="button" onClick={() => {
                  const toApply = (enrichmentReport.items || []).filter(it => it.selected);
                  toApply.forEach(it => {
                    if (it.apply.type === 'person') {
                      handlePersonChange(it.apply.key, it.value);
                    } else if (it.apply.type === 'company') {
                      handleCompanyChange(it.apply.key, it.value);
                    }
                  });
                  toast.success(`Applied ${toApply.length} field(s)`);
                  setEnrichmentReport({ isOpen: false, items: [], searchTerms: [], sources: '' });
                }} disabled={!enrichmentReport.items || enrichmentReport.items.filter(it => it.selected).length === 0} className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50">Apply Selected</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Help Modal */}
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
        title="คู่มือการจัดการลูกค้า"
        content={customerHelpContent}
      />

      {/* Duplicate Warning Modal */}
      <DuplicateWarningModal
        isOpen={duplicateWarning.show}
        onClose={() => setDuplicateWarning({ show: false, duplicates: [], pendingFormData: null })}
        duplicates={duplicateWarning.duplicates}
        onContinue={handleContinueWithSave}
        onUseExisting={handleUseExisting}
      />

      {/* Merge With Existing Customer Modal */}
      {mergeWithExisting.show && mergeWithExisting.existingCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {safeT('customers.mergeWithExisting', 'Merge with Existing Customer')}
                </h2>
              </div>
              <button
                onClick={() => setMergeWithExisting({ show: false, existingCustomer: null, pendingFormData: null, fieldSelections: {} })}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Info Message */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                      {safeT('customers.compareAndMerge', 'Compare and choose which values to keep')}
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      {safeT('customers.mergeInstructionsNew', 'For each field, select whether to keep the new value you entered or the existing value from the customer record.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Field Comparison Table */}
              <div className="space-y-4">
                {[
                  { key: 'FirstName', label: safeT('customers.firstName', 'First Name'), newKey: 'firstName' },
                  { key: 'LastName', label: safeT('customers.lastName', 'Last Name'), newKey: 'lastName' },
                  { key: 'FirstName_Eng', label: safeT('customers.firstNameEng', 'First Name (English)'), newKey: 'firstName_eng' },
                  { key: 'LastName_Eng', label: safeT('customers.lastNameEng', 'Last Name (English)'), newKey: 'lastName_eng' },
                  { key: 'Title', label: safeT('customers.title', 'Title'), newKey: 'title', existingAlt: 'PersonTitle' },
                  { key: 'Email', label: safeT('customers.email', 'Email'), newKey: 'email', existingAlt: 'PersonEmail' },
                  { key: 'Phone', label: safeT('customers.phone', 'Phone'), newKey: 'phone', existingAlt: 'PersonPhone' },
                  { key: 'JobTitle', label: safeT('customers.jobTitle', 'Job Title'), newKey: 'jobTitle' },
                  { key: 'Department', label: safeT('customers.department', 'Department'), newKey: 'department' },
                  { key: 'NickName', label: safeT('customers.nickName', 'Nick Name'), newKey: 'nickName' },
                ].map(field => {
                  const newVal = mergeWithExisting.pendingFormData?.person?.[field.newKey] || '';
                  const existingVal = mergeWithExisting.existingCustomer?.[field.key] || 
                                     mergeWithExisting.existingCustomer?.[field.existingAlt] || '';
                  const currentSelection = mergeWithExisting.fieldSelections[field.key] || '';
                  
                  // Check if values are different
                  const newValTrimmed = String(newVal).trim();
                  const existingValTrimmed = String(existingVal).trim();
                  const hasConflict = newValTrimmed && existingValTrimmed && newValTrimmed !== existingValTrimmed;
                  const onlyNew = newValTrimmed && !existingValTrimmed;
                  const onlyExisting = !newValTrimmed && existingValTrimmed;
                  const bothEmpty = !newValTrimmed && !existingValTrimmed;

                  if (bothEmpty) return null; // Skip empty fields

                  return (
                    <div 
                      key={field.key}
                      className={`border rounded-lg p-4 ${
                        hasConflict 
                          ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10' 
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {field.label}
                        </label>
                        {hasConflict && (
                          <span className="text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded">
                            {safeT('customers.conflictingValues', 'Different values')}
                          </span>
                        )}
                      </div>
                      
                      {hasConflict ? (
                        // Show radio buttons for conflicting values
                        <div className="space-y-2">
                          <label 
                            className={`flex items-center space-x-3 p-2 rounded cursor-pointer transition-colors ${
                              currentSelection === newValTrimmed
                                ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            <input
                              type="radio"
                              name={field.key}
                              checked={currentSelection === newValTrimmed}
                              onChange={() => handleMergeFieldChange(field.key, newValTrimmed)}
                              className="h-4 w-4 text-green-600 focus:ring-green-500"
                            />
                            <span className="flex-1 text-gray-900 dark:text-white">{newValTrimmed}</span>
                            <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                              {safeT('customers.newValue', 'New')}
                            </span>
                          </label>
                          <label 
                            className={`flex items-center space-x-3 p-2 rounded cursor-pointer transition-colors ${
                              currentSelection === existingValTrimmed
                                ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            <input
                              type="radio"
                              name={field.key}
                              checked={currentSelection === existingValTrimmed}
                              onChange={() => handleMergeFieldChange(field.key, existingValTrimmed)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="flex-1 text-gray-900 dark:text-white">{existingValTrimmed}</span>
                            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                              {safeT('customers.existingValue', 'Existing')}
                            </span>
                          </label>
                        </div>
                      ) : (
                        // Single value - just display it
                        <div className="flex items-center justify-between">
                          <span className="text-gray-900 dark:text-white">
                            {currentSelection || '-'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            onlyNew 
                              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                              : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          }`}>
                            {onlyNew ? safeT('customers.newValue', 'New') : safeT('customers.existingValue', 'Existing')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Final Summary */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  {safeT('customers.finalValues', 'Final Values')}
                </h3>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {Object.entries(mergeWithExisting.fieldSelections).map(([key, value]) => (
                      value && (
                        <div key={key}>
                          <span className="text-gray-500 dark:text-gray-400">{key}: </span>
                          <span className="text-gray-900 dark:text-white font-medium">{value}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setMergeWithExisting({ show: false, existingCustomer: null, pendingFormData: null, fieldSelections: {} })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirmMergeWithExisting}
                disabled={formLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {formLoading ? safeT('common.processing', 'Processing...') : safeT('customers.updateExistingCustomer', 'Update Customer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerForm; 